import React, { useEffect, Suspense } from 'react';
import { initAppDownloadTracking } from './lib/analytics';
import { lazyRetry } from './lib/lazyWithRetry';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Critical pages - loaded immediately
import HomePage from './pages/HomePage';
import SitemapPage from './pages/SitemapPage';
import LifeStageIndexPage from './pages/LifeStageIndexPage';
import LifeStageHubPage from './pages/LifeStageHubPage';
import MyBuildPilotPage from './pages/MyBuildPilotPage';
import CostToBuildAustraliaPage from './pages/national/CostToBuildAustraliaPage';
import HowToBuildAHomePage from './pages/national/HowToBuildAHomePage';
import RenovationCostsAustraliaPage from './pages/national/RenovationCostsAustraliaPage';
import KnockdownRebuildExplainedPage from './pages/national/KnockdownRebuildExplainedPage';
import FirstHomeBuyerAustraliaPage from './pages/national/FirstHomeBuyerAustraliaPage';
import GrannyFlatCostsAustraliaPage from './pages/national/GrannyFlatCostsAustraliaPage';
import { SEO_LANDING_SLUGS, SeoLandingPageBySlug } from './pages/seoLandingPages';
import SourceIndexPage from './pages/SourceIndexPage';
import EditorialNotebookPage from './pages/EditorialNotebookPage';
import LifecyclePillarPage from './pages/LifecyclePillarPage';
import ContactPage from './pages/ContactPage';
import AdvertisePage from './pages/AdvertisePage';
import CookieConsent from './components/CookieConsent';
import SaVicHreflangPair from './components/SaVicHreflangPair';
import TermsReacceptBanner from './components/legal/TermsReacceptBanner';
import ContactModal from './components/ContactModal';
import AuthModal from './components/auth/AuthModal';
import ScrollToTop from './components/ScrollToTop';
import SAContactGuard from './components/geo/SAContactGuard';
import LinkPrefetcher from './components/LinkPrefetcher';
import MetaDeduper from './components/seo/MetaDeduper';
import { ContactModalProvider } from './context/ContactModalContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { FavouritesProvider } from './context/FavouritesContext';
import { CompareProvider } from './context/CompareContext';
import { CompareTrayProvider } from './context/CompareTrayContext';
import CompareTrayBar from './components/compare/CompareTrayBar';
import CompareBar from './components/CompareBar';
import FavouritesSidebar from './components/FavouritesSidebar';
import FavouritesFab from './components/FavouritesFab';
import StageAwareToolStrip from './components/StageAwareToolStrip';
import { OrganizationSchema } from './components/seo/SiteSchema';
import { getAllProgrammaticSlugs } from './data/programmaticSeoData';
import { AuthProvider } from './workspace/context/AuthContext';
import { useMarketingAuth } from './hooks/useMarketingAuth';
import QuickAddFab from './workspace/components/QuickAddFab';
// BackToDashboardChip is mounted inside WorkspaceLayout (not globally).
import SmartTriggers from './components/recommendations/SmartTriggers';
import DeepLinkMatcher from './components/recommendations/DeepLinkMatcher';
import StateContextGate from './components/seo/StateContextGate';
import { useIsSA } from './lib/userState';
import LocationGate from './components/geo/LocationGate';
import AdelaideUrlHeadsUpStrip from './components/seo/AdelaideUrlHeadsUpStrip';
import ReciprocalGeoAlternate from './components/seo/ReciprocalGeoAlternate';
import GlassFilterDefs from './components/media/GlassFilterDefs';
import './workspace/workspace.css';

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-white" data-testid="page-loader">
    {/* Indeterminate top progress bar keeps the transition feeling alive */}
    <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-[#1d8efe]/15 overflow-hidden">
      <div className="h-full w-1/3 bg-[#1d8efe] bp-loadbar" />
    </div>
    {/* Header shell placeholder so the frame never blinks out */}
    <div className="h-16 border-b border-gray-100 flex items-center px-5 sm:px-6">
      <div className="h-6 w-32 rounded-md bg-gray-200 animate-pulse" />
      <div className="ml-auto hidden md:flex gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-4 w-16 rounded bg-gray-100 animate-pulse" />
        ))}
      </div>
    </div>
    {/* Content skeleton mimics a typical page so it reads as "filling in" */}
    <div className="max-w-[1000px] mx-auto px-5 sm:px-6 py-12">
      <div className="h-3.5 w-24 rounded bg-gray-100 animate-pulse mb-5" />
      <div className="h-10 w-3/4 rounded-lg bg-gray-200 animate-pulse mb-3" />
      <div className="h-10 w-1/2 rounded-lg bg-gray-200 animate-pulse mb-10" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rounded-2xl border border-gray-100 p-5">
            <div className="h-24 rounded-xl bg-gray-100 animate-pulse mb-4" />
            <div className="h-4 w-2/3 rounded bg-gray-200 animate-pulse mb-2.5" />
            <div className="h-3 w-full rounded bg-gray-100 animate-pulse mb-1.5" />
            <div className="h-3 w-4/5 rounded bg-gray-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Permanent redirect helper: /blog/:slug -> /post/:slug (no 404 for any
// inbound link that uses the old `/blog/` path).
const BlogPostRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/post/${slug}`} replace />;
};

// 2026-02 - Forward `/adelaide-suburbs/<suburb>` to the canonical
// `/home-builders-in/<suburb>` so any external SEO/AI references to
// the bare slug land on the right hub instead of the 404 page.
const AdelaideSuburbRedirect = () => {
  const { suburbSlug } = useParams();
  return <Navigate to={`/home-builders-in/${suburbSlug}`} replace />;
};

// 2026-06 - VIC H&L suburb pages moved from /vic/house-and-land/<slug>
// to the Adelaide-parallel /house-and-land-melbourne/<slug> canonical.
// Client redirect (server 301 lives in public/_redirects) so the old
// indexed URL preserves its ranking.
const VicHnlSuburbRedirect = () => {
  const { suburbSlug } = useParams();
  return <Navigate to={`/house-and-land-melbourne/${suburbSlug}`} replace />;
};

// 2026-02 - `/find/supplier/<x>` is a legacy shape; canonical is
// `/find/<subSlug>`. The known case is `solar` → `solar-installer`.
const FindSupplierRedirect = () => {
  const { slug } = useParams();
  const map = { solar: 'solar-installer' };
  return <Navigate to={`/find/${map[slug] || slug}`} replace />;
};

// Lazy-loaded pages for better initial bundle size
const BuildingTypePage = lazyRetry(() => import('./pages/BuildingTypePage'));
// SEO programmatic building-type surface (13 types + suburb matrix + comparisons)
const BuildingTypeSeoHubPage = lazyRetry(() => import('./pages/BuildingTypeSeoHubPage'));
const BuildingTypeInSuburbPage = lazyRetry(() => import('./pages/BuildingTypeInSuburbPage'));
const BuildingTypeComparisonPage = lazyRetry(() => import('./pages/BuildingTypeComparisonPage'));
const JourneyLandingPage = lazyRetry(() => import('./pages/JourneyLandingPage'));
const BuildingDisputesPage = lazyRetry(() => import('./pages/BuildingDisputesPage'));
const TermsPage = lazyRetry(() => import('./pages/TermsPage'));
const PrivacyPage = lazyRetry(() => import('./pages/PrivacyPage'));
const MatchingPolicyPage = lazyRetry(() => import('./pages/legal/MatchingPolicyPage'));
const ComplaintsProcessPage = lazyRetry(() => import('./pages/legal/ComplaintsProcessPage'));
const MarketNotesIndexPage = lazyRetry(() => import('./pages/MarketNotesIndexPage'));
const MarketNoteDetailPage = lazyRetry(() => import('./pages/MarketNoteDetailPage'));
const NewThisMonthPage = lazyRetry(() => import('./pages/NewThisMonthPage'));
const BuildChoicesIndexPage = lazyRetry(() => import('./pages/BuildChoicesIndexPage'));
const CompareOptionsPage = lazyRetry(() => import('./pages/CompareOptionsPage'));
const BuildChoiceGuidePage = lazyRetry(() => import('./pages/BuildChoiceGuidePage'));
const AffiliateDisclosurePage = lazyRetry(() => import('./pages/legal/AffiliateDisclosurePage'));

// ===== Workspace (logged-in app area at /workspace/*) =====
// All workspace code is code-split so the marketing site stays light.
const WorkspaceShell = lazyRetry(() => import('./workspace/WorkspaceShell'));
const WorkspaceWelcomePage = lazyRetry(() => import('./workspace/pages/WelcomePage'));
const WorkspaceLoginPage = lazyRetry(() => import('./workspace/pages/LoginPage'));
const WorkspaceSignupPage = lazyRetry(() => import('./workspace/pages/SignupPage'));
const WorkspaceForgotPasswordPage = lazyRetry(() => import('./workspace/pages/ForgotPasswordPage'));
const WorkspaceAuthCallbackPage = lazyRetry(() => import('./workspace/pages/AuthCallbackPage'));
const WorkspaceOnboardingPage = lazyRetry(() => import('./workspace/pages/OnboardingPage'));
const WorkspaceLandStatusGatePage = lazyRetry(() => import('./workspace/pages/LandStatusGatePage'));
const WorkspaceHouseLandOfferGatePage = lazyRetry(() => import('./workspace/pages/HouseLandOfferGatePage'));
const WorkspaceFhbGatePage = lazyRetry(() => import('./workspace/pages/FhbGatePage'));
const WorkspaceLegalConsentPage = lazyRetry(() => import('./workspace/pages/LegalConsentPage'));
const WorkspacePhoneVerificationPage = lazyRetry(() => import('./workspace/pages/PhoneVerificationPage'));
const WorkspaceDesignCategoryPage = lazyRetry(() => import('./workspace/pages/DesignCategoryPage'));
const WorkspaceDesignGeneratorPage = lazyRetry(() => import('./workspace/pages/DesignGeneratorPage'));

// Route selector: 8 categories with full generator schemas use the AI
// generator page; 'homes' (and any unknown slug) keep the existing
// gallery-style category page.
const GENERATOR_CATEGORIES = new Set([
  'pergola', 'pool', 'decking', 'landscaping',
  'shed-garage', 'interior', 'mood-board', 'room-layout',
]);
const WorkspaceDesignRouteSelector = () => {
  const { category } = useParams();
  if (GENERATOR_CATEGORIES.has(category)) return <WorkspaceDesignGeneratorPage />;
  return <WorkspaceDesignCategoryPage />;
};
const WorkspaceLayout = lazyRetry(() => import('./workspace/components/WorkspaceLayout'));
const WorkspaceDashboardPage = lazyRetry(() => import('./workspace/pages/DashboardPage'));
const WorkspaceComingSoonPage = lazyRetry(() => import('./workspace/pages/ComingSoonPage'));
const WorkspaceToolboxPage = lazyRetry(() => import('./workspace/pages/ToolboxPage'));
const WorkspaceAdvicePage = lazyRetry(() => import('./workspace/pages/AdvicePage'));
const WorkspaceDesignsPage = lazyRetry(() => import('./workspace/pages/DesignsPage'));
const WorkspaceCostTrackerPage = lazyRetry(() => import('./workspace/pages/CostTrackerPage'));
const WorkspaceHomeLogPage = lazyRetry(() => import('./workspace/pages/HomeLogPage'));
const WorkspaceMilestonesPage = lazyRetry(() => import('./workspace/pages/MilestonesPage'));
const WorkspaceChecklistsPage = lazyRetry(() => import('./workspace/pages/ChecklistsPage'));
const WorkspaceDocumentsPage = lazyRetry(() => import('./workspace/pages/DocumentsPage'));
const WorkspaceContactsPage = lazyRetry(() => import('./workspace/pages/ContactsPage'));
const WorkspaceBillingPage = lazyRetry(() => import('./workspace/pages/BillingPage'));
const WorkspaceBillingSuccessPage = lazyRetry(() => import('./workspace/pages/BillingSuccessPage'));
const WorkspaceProfilePage = lazyRetry(() => import('./workspace/pages/ProfilePage'));
const WorkspaceNotificationsPage = lazyRetry(() => import('./workspace/pages/NotificationsPage'));
const WorkspaceMySavedPage = lazyRetry(() => import('./workspace/pages/MySavedPage'));
const WorkspaceSettingsPage = lazyRetry(() => import('./workspace/pages/SettingsPage'));
const WorkspaceProjectSetupPage = lazyRetry(() => import('./workspace/pages/ProjectSetupPage'));
const WorkspaceHelpPage = lazyRetry(() => import('./workspace/pages/HelpPage'));
const WorkspaceUpdatesPage = lazyRetry(() => import('./workspace/pages/UpdatesPage'));
const WorkspaceAdviceChatPage = lazyRetry(() => import('./workspace/pages/AdviceChatPage'));
const WorkspaceIntelligencePage = lazyRetry(() => import('./workspace/pages/IntelligencePage'));
const WorkspaceHouseAndLandMatchmakerPage = lazyRetry(() => import('./workspace/pages/HouseAndLandMatchmakerPage'));
const WorkspaceFavouriteHomesPage = lazyRetry(() => import('./workspace/pages/FavouriteHomesPage'));
const WorkspaceMyMatchesPage = lazyRetry(() => import('./workspace/pages/MyMatchesPage'));
const WorkspaceFacadeVisualiserPage = lazyRetry(() => import('./workspace/pages/FacadeVisualiserPage'));
const WorkspaceQuoteComparisonPage = lazyRetry(() => import('./workspace/pages/QuoteComparisonPage'));
const WorkspaceBuildProgressPage = lazyRetry(() => import('./workspace/pages/BuildProgressPage'));
const WorkspaceGetQuotePage = lazyRetry(() => import('./workspace/pages/GetQuotePage'));
const WorkspaceFindABuilderPage = lazyRetry(() => import('./workspace/pages/FindABuilderPage'));
const WorkspaceHouseAndLandPage = lazyRetry(() => import('./workspace/pages/HouseAndLandPage'));
const WorkspaceBuilderLicenceCheckPage = lazyRetry(() => import('./workspace/pages/BuilderLicenceCheckPage'));
const WorkspaceBuildingInsuranceCheckPage = lazyRetry(() => import('./workspace/pages/BuildingInsuranceCheckPage'));
const WorkspaceGovernmentResourcesPage = lazyRetry(() => import('./workspace/pages/GovernmentResourcesPage'));
const WorkspaceSolarOrientationPage = lazyRetry(() => import('./workspace/pages/SolarOrientationPage'));
const WorkspaceLifestyleDiscoveryPage = lazyRetry(() => import('./workspace/pages/LifestyleDiscoveryPage'));
const WorkspaceFhbResultsPage = lazyRetry(() => import('./workspace/pages/FhbResultsPage'));
const WorkspaceDefectsScannerPage = lazyRetry(() => import('./workspace/pages/DefectsScannerPage'));
const WorkspaceFurniturePlannerPage = lazyRetry(() => import('./workspace/pages/FurniturePlannerPage'));
const WorkspaceInterior3DPage = lazyRetry(() => import('./workspace/pages/Interior3DPage'));
const WorkspaceFindATradiePage = lazyRetry(() => import('./workspace/pages/FindATradiePage'));
const WorkspaceFindASupplierPage = lazyRetry(() => import('./workspace/pages/FindASupplierPage'));
const WorkspaceFindABusinessPage = lazyRetry(() => import('./workspace/pages/FindABusinessPage'));
const WorkspaceDashboardPlaceholder = lazyRetry(() => import('./workspace/pages/DashboardPlaceholder'));
const WorkspaceProtectedRoute = lazyRetry(() => import('./workspace/components/ProtectedRoute'));
const CopyrightPage = lazyRetry(() => import('./pages/CopyrightPage'));
const CookiePage = lazyRetry(() => import('./pages/CookiePage'));
const BusinessEnquiryPage = lazyRetry(() => import('./pages/BusinessEnquiryPage'));
const TerminologyPage = lazyRetry(() => import('./pages/TerminologyPage'));
const BuildingInspectorsPage = lazyRetry(() => import('./pages/BuildingInspectorsPage'));
const WhereWeFitInPage = lazyRetry(() => import('./pages/WhereWeFitInPage'));
const TrustedBuildersPage = lazyRetry(() => import('./pages/TrustedBuildersPage'));
const WhyBuildPilotPage = lazyRetry(() => import('./pages/WhyBuildPilotPage'));
const FAQsPage = lazyRetry(() => import('./pages/FAQsPage'));
const ResultsPage = lazyRetry(() => import('./pages/ResultsPage'));
const BlogListingPage = lazyRetry(() => import('./pages/BlogListingPage'));
const BlogArticlePage = lazyRetry(() => import('./pages/BlogArticlePage'));

// SEO Landing Pages - lazy loaded
const HomeBuildersAdelaide = lazyRetry(() => import('./pages/HomeBuildersAdelaide'));
const SuburbBuildingPage = lazyRetry(() => import('./pages/SuburbBuildingPage'));
const QuestionsToAskBuildersPage = lazyRetry(() => import('./pages/QuestionsToAskBuildersPage'));
const HiringTradesGuide = lazyRetry(() => import('./pages/HiringTradesGuide'));
const HiringProfessionalsGuide = lazyRetry(() => import('./pages/HiringProfessionalsGuide'));
const VerifyABusinessPage = lazyRetry(() => import('./pages/VerifyABusinessPage'));
const BuildingCostsAdelaidePage = lazyRetry(() => import('./pages/BuildingCostsAdelaidePage'));
const AdelaideBuildCostReportPage = lazyRetry(() => import('./pages/AdelaideBuildCostReportPage'));
const BuildingPartnersPage = lazyRetry(() => import('./pages/BuildingPartnersPage'));
const HomeBuildersNearMePage = lazyRetry(() => import('./pages/HomeBuildersNearMePage'));
const FirstHomeBuyerPage = lazyRetry(() => import('./pages/FirstHomeBuyerPage'));

// AI SEO / GEO landing pages (programmatic + authority)
const AdelaideBuildersIndexPage = lazyRetry(() => import('./pages/AdelaideBuildersIndexPage'));
const AIAgentsPage = lazyRetry(() => import('./pages/AIAgentsPage'));
const AdminOnboardingPage = lazyRetry(() => import('./pages/admin/AdminOnboardingPage'));const BusinessOnboardingPage = lazyRetry(() => import('./pages/BusinessOnboardingPage'));
const BuilderInSuburbPage = lazyRetry(() => import('./pages/BuilderInSuburbPage'));
const HomeDesignsLandingPage = lazyRetry(() => import('./pages/HomeDesignsLandingPage'));
const EditorialTeamPage = lazyRetry(() => import('./pages/EditorialTeamPage'));

// Phase 1 legal / transparency pages
const OneToOneSupportAgreementPage = lazyRetry(() => import('./pages/legal/OneToOneSupportAgreementPage'));
const RecommendationsPolicyPage = lazyRetry(() => import('./pages/legal/RecommendationsPolicyPage'));
const AdvertisingPolicyPage = lazyRetry(() => import('./pages/legal/AdvertisingPolicyPage'));
const CommercialDisclosurePage = lazyRetry(() => import('./pages/legal/CommercialDisclosurePage'));
const PropertyListingsPolicyPage = lazyRetry(() => import('./pages/legal/PropertyListingsPolicyPage'));
const HowWeMakeMoneyPage = lazyRetry(() => import('./pages/HowWeMakeMoneyPage'));
const BusinessAdvertisePage = lazyRetry(() => import('./pages/business/BusinessAdvertisePage'));

// Redirect component for /glossary/:termSlug to /terminology/:termSlug
const GlossaryRedirect = () => {
  const { termSlug } = useParams();
  return <Navigate to={`/terminology/${termSlug}`} replace />;
};

// Permanent redirect for old /estates/:slug -> /land-estate/:slug
const EstateLegacyRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/land-estate/${slug}`} replace />;
};

// Supporting Pages - Knockdown Rebuild Hub
const KDRWhenToKnockdown = lazyRetry(() => import('./pages/supporting/KDRWhenToKnockdown'));
const KnockdownRebuildGuide = lazyRetry(() => import('./pages/KnockdownRebuildGuide'));
const SlopingBlockGuidePage = lazyRetry(() => import('./pages/SlopingBlockGuidePage'));
const HomeExtensionsAdelaideGuidePage = lazyRetry(() => import('./pages/HomeExtensionsAdelaideGuidePage'));
const ExtendOrRebuildAdelaidePage = lazyRetry(() => import('./pages/ExtendOrRebuildAdelaidePage'));
const RenovateOrRebuildAdelaidePage = lazyRetry(() => import('./pages/RenovateOrRebuildAdelaidePage'));
const GrannyFlatVsExtensionAdelaidePage = lazyRetry(() => import('./pages/GrannyFlatVsExtensionAdelaidePage'));
const ExtendOrRebuildMelbournePage = lazyRetry(() => import('./pages/vic/ExtendOrRebuildMelbournePage'));
const RenovateOrRebuildMelbournePage = lazyRetry(() => import('./pages/vic/RenovateOrRebuildMelbournePage'));
const ProjectVsCustomBuilderPage = lazyRetry(() => import('./pages/ProjectVsCustomBuilderPage'));
const NarrowBlockBuildersAdelaidePage = lazyRetry(() => import('./pages/NarrowBlockBuildersAdelaidePage'));
const DownsizerHomeBuildersAdelaidePage = lazyRetry(() => import('./pages/DownsizerHomeBuildersAdelaidePage'));
const PassiveHouseBuildersAdelaidePage = lazyRetry(() => import('./pages/PassiveHouseBuildersAdelaidePage'));
const CharacterHomeRenovationAdelaidePage = lazyRetry(() => import('./pages/CharacterHomeRenovationAdelaidePage'));
const RestoringPeriodHomePage = lazyRetry(() => import('./pages/RestoringPeriodHomePage'));
const HomeStylesHubPage = lazyRetry(() => import('./pages/HomeStylesHubPage'));
const HomeStyleGuidePage = lazyRetry(() => import('./pages/HomeStyleGuidePage'));
const BushfireBALBuildersAdelaidePage = lazyRetry(() => import('./pages/BushfireBALBuildersAdelaidePage'));
const DuplexBuildersAdelaidePage = lazyRetry(() => import('./pages/DuplexBuildersAdelaidePage'));
const SplitLevelBuildersAdelaidePage = lazyRetry(() => import('./pages/SplitLevelBuildersAdelaidePage'));
const ModularPrefabBuildersAdelaidePage = lazyRetry(() => import('./pages/ModularPrefabBuildersAdelaidePage'));
const ClusterHowToPage = lazyRetry(() => import('./pages/ClusterHowToPage'));
const ProfessionalPillarPage = lazyRetry(() => import('./pages/ProfessionalPillarPage'));
const BuildingTypePillarPage = lazyRetry(() => import('./pages/BuildingTypePillarPage'));
const ProjectTypePillarPage = lazyRetry(() => import('./pages/ProjectTypePillarPage'));
const GrannyFlatGuidePage = lazyRetry(() => import('./pages/GrannyFlatGuidePage'));
const EnergyEfficientHomeGuidePage = lazyRetry(() => import('./pages/EnergyEfficientHomeGuidePage'));
const ShowerScreensBuyingGuidePage = lazyRetry(() => import('./pages/ShowerScreensBuyingGuidePage'));
const KitchenBenchtopsBuyingGuidePage = lazyRetry(() => import('./pages/KitchenBenchtopsBuyingGuidePage'));
const HomeSecurityCamerasBuyingGuidePage = lazyRetry(() => import('./pages/HomeSecurityCamerasBuyingGuidePage'));
const CleaningFloorsBuyingGuidePage = lazyRetry(() => import('./pages/CleaningFloorsBuyingGuidePage'));
const CurtainsBlindsBuyingGuidePage = lazyRetry(() => import('./pages/CurtainsBlindsBuyingGuidePage'));
const NewHomeFitOutGuidePage = lazyRetry(() => import('./pages/NewHomeFitOutGuidePage'));
const BackupPowerNewBuildGuidePage = lazyRetry(() => import('./pages/BackupPowerNewBuildGuidePage'));
const HomeSecurityNewBuildGuidePage = lazyRetry(() => import('./pages/HomeSecurityNewBuildGuidePage'));
const WindowFurnishingsNewBuildGuidePage = lazyRetry(() => import('./pages/WindowFurnishingsNewBuildGuidePage'));
const KitchenFitOutNewBuildGuidePage = lazyRetry(() => import('./pages/KitchenFitOutNewBuildGuidePage'));
const GuidesIndexPage = lazyRetry(() => import('./pages/GuidesIndexPage'));
const KDRCosts = lazyRetry(() => import('./pages/supporting/KDRCosts'));
const KDRCouncilApprovals = lazyRetry(() => import('./pages/supporting/KDRCouncilApprovals'));
const KDRDemolitionProcess = lazyRetry(() => import('./pages/supporting/KDRDemolitionProcess'));

// Sprint 1 - Platform Hubs (Builders / Designs / Suburbs / Guides / Building Types)
const HubPage = lazyRetry(() => import('./pages/hubs/HubPage'));
const BuildersHub = lazyRetry(() => import('./pages/hubs/BuildersHub'));
const BuilderSpotlight = lazyRetry(() => import('./pages/hubs/BuilderSpotlight'));
const SuburbsHub = lazyRetry(() => import('./pages/hubs/SuburbsHub'));
const DesignsHub = lazyRetry(() => import('./pages/hubs/DesignsHub'));
const InspirationHub = lazyRetry(() => import('./pages/inspiration/InspirationHub'));
const BuildTypesIndex = lazyRetry(() => import('./pages/inspiration/BuildTypesIndex'));
const BuildTypeDesignPage = lazyRetry(() => import('./pages/inspiration/BuildTypeDesignPage'));
const InspirationProjectPage = lazyRetry(() => import('./pages/inspiration/InspirationProjectPage'));
const JourneyPage = lazyRetry(() => import('./pages/hubs/JourneyPage'));
const OneOnOneSupportPage = lazyRetry(() => import('./pages/OneOnOneSupportPage'));
const WatchHubPage = lazyRetry(() => import('./pages/WatchHubPage'));
const WatchEpisodePage = lazyRetry(() => import('./pages/WatchEpisodePage'));
const QuestionsIndexPage = lazyRetry(() => import('./pages/QuestionsIndexPage'));
const QuestionPage = lazyRetry(() => import('./pages/QuestionPage'));
const CommunityPage = lazyRetry(() => import('./pages/CommunityPage'));

// Suppliers Hub + supporting pages
const SuppliersHub = lazyRetry(() => import('./pages/suppliers/SuppliersHub'));
const SupplierCategoryPage = lazyRetry(() => import('./pages/suppliers/SupplierCategoryPage'));
const SupplierGuidePage = lazyRetry(() => import('./pages/suppliers/SupplierGuidePage'));
// Unified DB-driven business profile page - one component for tradies,
// professionals + any future business type onboarded via the
// onboard_business.py script. Suppliers fall through to this from
// SupplierDetailPage when no static entry exists.
const BusinessProfilePage = lazyRetry(() => import('./pages/BusinessProfilePage'));
const LearnGuidePage = lazyRetry(() => import('./pages/LearnGuidePage'));
const LearnIndexPage = lazyRetry(() => import('./pages/LearnIndexPage'));
const ProductsPage = lazyRetry(() => import('./pages/ProductsPage'));
const NewHomeSetupChecklistPage = lazyRetry(() => import('./pages/NewHomeSetupChecklistPage'));
const ProductsCategoryPage = lazyRetry(() => import('./pages/ProductsCategoryPage'));
const LearnClusterHubPage = lazyRetry(() => import('./pages/LearnClusterHubPage'));

// House and Land Packages (live BuildPilot inventory, gated detail behind app)
const PackagesHubPage = lazyRetry(() => import('./pages/packages/PackagesHubPage'));
const PackageSuburbPage = lazyRetry(() => import('./pages/packages/PackageSuburbPage'));
const PackageDetailPage = lazyRetry(() => import('./pages/packages/PackageDetailPage'));
const PackageFilterPage = lazyRetry(() => import('./pages/packages/PackageFilterPage'));
const SuburbFilterPage = lazyRetry(() => import('./pages/packages/SuburbFilterPage'));
const VicRegionHubPage = lazyRetry(() => import('./pages/packages/VicRegionHubPage'));
const LandHubPage = lazyRetry(() => import('./pages/land/LandHubPage'));
const LandListingDetailPage = lazyRetry(() => import('./pages/land/LandListingDetailPage'));

// VIC, launch scaffolding. See VIC_LAUNCH_CHECKLIST.md.
const VicHubPage = lazyRetry(() => import('./pages/vic/VicHubPage'));
const VicGrantsPage = lazyRetry(() => import('./pages/vic/VicGrantsPage'));
const VicCouncilGuidePage = lazyRetry(() => import('./pages/vic/VicCouncilGuidePage'));
// Sprint 1 Melbourne SEO money pages
const FirstHomeBuyerMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/FirstHomeBuyerMelbournePage'));
const MelbourneBuildCostReport2026Page = lazyRetry(() => import('./pages/vic/money-pages/MelbourneBuildCostReport2026Page'));
const HomeBuildersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/HomeBuildersMelbournePage'));
const KnockdownRebuildMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/KnockdownRebuildMelbournePage'));
const HouseAndLandMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/HouseAndLandMelbournePage'));
const HLBudgetMelbournePage = lazyRetry(() => import('./pages/vic/HLBudgetMelbournePage'));
const BuildTimesMelbournePage = lazyRetry(() => import('./pages/vic/BuildTimesMelbournePage'));
const BedroomPricingMelbournePage = lazyRetry(() => import('./pages/vic/BedroomPricingMelbournePage'));
const MelbourneBuilderTypePage = lazyRetry(() => import('./pages/vic/MelbourneBuilderTypePage'));
const BestNewHomeBuildersVicPage = lazyRetry(() => import('./pages/vic/BestNewHomeBuildersVicPage'));
const MelbourneDisplayHomesPage = lazyRetry(() => import('./pages/vic/MelbourneDisplayHomesPage'));
const MelbourneGrowthSuburbsPage = lazyRetry(() => import('./pages/vic/MelbourneGrowthSuburbsPage'));
const AdelaideVsMelbourneBuildPage = lazyRetry(() => import('./pages/vic/money-pages/AdelaideVsMelbourneBuildPage'));
const AdelaideVsMelbourneComparatorPage = lazyRetry(() => import('./pages/AdelaideVsMelbourneComparatorPage'));
const BestSuburbsToBuildMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/BestSuburbsToBuildMelbournePage'));
const MelbourneBuildingCostsPage = lazyRetry(() => import('./pages/vic/money-pages/MelbourneBuildingCostsPage'));
const ArchitectsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/ArchitectsMelbournePage'));
const BuildingInspectorsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/BuildingInspectorsMelbournePage'));
// VIC Phase A final 5 hub pages (2026-03-05)
const MelbourneDisplayHomeDirectoryPage = lazyRetry(() => import('./pages/vic/money-pages/MelbourneDisplayHomeDirectoryPage'));
const MelbourneHomeBuilderTypesPage = lazyRetry(() => import('./pages/vic/money-pages/MelbourneHomeBuilderTypesPage'));
const HowToChooseHomeBuilderMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/HowToChooseHomeBuilderMelbournePage'));
// VIC guides parity (2026-06): budget-capacity + builder/contract decision + land guides.
const WhatCanYouBuildFor500kMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/WhatCanYouBuildFor500kMelbournePage'));
const WhatCanYouBuildFor650kMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/WhatCanYouBuildFor650kMelbournePage'));
const WhatCanYouBuildFor850kMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/WhatCanYouBuildFor850kMelbournePage'));
const FixedPriceVsCostPlusMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/FixedPriceVsCostPlusMelbournePage'));
const VolumeVsCustomBuilderMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/VolumeVsCustomBuilderMelbournePage'));
const ChoosingLandMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/ChoosingLandMelbournePage'));
const OwnerBuilderMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/OwnerBuilderMelbournePage'));
const SiteCostsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/SiteCostsMelbournePage'));
const SevenStarEnergyMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/SevenStarEnergyMelbournePage'));
const MelbourneCorridorHubPage = lazyRetry(() => import('./pages/vic/MelbourneCorridorHubPage'));
const MelbourneCorridorsOverviewPage = lazyRetry(() => import('./pages/vic/MelbourneCorridorsOverviewPage'));
const MelbourneBuildingMarketReport = lazyRetry(() => import('./pages/vic/MelbourneBuildingMarketReport'));
const MelbourneDataPage = lazyRetry(() => import('./pages/vic/MelbourneDataPage'));
// VIC Phase B + C (2026-03-06): 8 climate + layout hubs, 15 specialist "what to look for" pages.
// Both driven by shared thin wrappers + data files. Info-only, no builder brand names.
const MelbourneClimateOrLayoutPage = lazyRetry(() => import('./pages/vic/MelbourneClimateOrLayoutPage'));
const MelbourneSpecialistPage = lazyRetry(() => import('./pages/vic/MelbourneSpecialistPage'));
const AdelaideSpecialistPage = lazyRetry(() => import('./pages/AdelaideSpecialistPage'));

// Search results page + admin search insights
const SearchResultsPage = lazyRetry(() => import('./pages/SearchResultsPage'));
const SearchInsightsPage = lazyRetry(() => import('./pages/admin/SearchInsightsPage'));
const BuilderDriftPage = lazyRetry(() => import('./pages/admin/BuilderDriftPage'));
const StrikingDistancePage = lazyRetry(() => import('./pages/admin/StrikingDistancePage'));
const AdvertiserPerformancePage = lazyRetry(() => import('./pages/admin/AdvertiserPerformancePage'));

// New SEO Pages - Research & Comparison Keywords
const WhichBuilderPage = lazyRetry(() => import('./pages/WhichBuilderPage'));
const BuildTimesPage = lazyRetry(() => import('./pages/BuildTimesPage'));
const CommonBuilderProblemsPage = lazyRetry(() => import('./pages/CommonBuilderProblemsPage'));
const BestBuilderForSuburbPage = lazyRetry(() => import('./pages/BestBuilderForSuburbPage'));

// Sprint 3 (2026-06-03), /find anonymous-funnel pages
const FindHub = lazyRetry(() => import('./pages/FindHub'));
const FindSuperCategory = lazyRetry(() => import('./pages/FindSuperCategory'));
const FindCategory = lazyRetry(() => import('./pages/FindCategory'));

// Supporting Pages - Choosing Land Hub
const LandSiteCosts = lazyRetry(() => import('./pages/supporting/LandSiteCosts'));
const LandSoilTypes = lazyRetry(() => import('./pages/supporting/LandSoilTypes'));
const LandBlockAssessment = lazyRetry(() => import('./pages/supporting/LandBlockAssessment'));

// Supporting Pages - Building a New Home Hub
const NewHomeBuilderSelection = lazyRetry(() => import('./pages/supporting/NewHomeBuilderSelection'));
const NewHomeContracts = lazyRetry(() => import('./pages/supporting/NewHomeContracts'));
const NewHomeBuildTimeline = lazyRetry(() => import('./pages/supporting/NewHomeBuildTimeline'));
const NewHomeBudget = lazyRetry(() => import('./pages/supporting/NewHomeBudget'));
const NewHomeHouseAndLand = lazyRetry(() => import('./pages/supporting/NewHomeHouseAndLand'));

// Supporting Pages - Renovations Hub
const RenovationCosts = lazyRetry(() => import('./pages/supporting/RenovationCosts'));
const RenovationChoosingRenovator = lazyRetry(() => import('./pages/supporting/RenovationChoosingRenovator'));
const ImprovementCostGuide = lazyRetry(() => import('./pages/supporting/ImprovementCostGuide'));
const ImprovementChoosingGuide = lazyRetry(() => import('./pages/supporting/ImprovementChoosingGuide'));
const ImprovementPlanningGuide = lazyRetry(() => import('./pages/supporting/ImprovementPlanningGuide'));
const HomeImprovementHub = lazyRetry(() => import('./pages/supporting/HomeImprovementHub'));
const RenovationPlanning = lazyRetry(() => import('./pages/supporting/RenovationPlanning'));
const RenovationLivingThrough = lazyRetry(() => import('./pages/supporting/RenovationLivingThrough'));

// South Australia Suburb Pages
const SouthAustraliaPage = lazyRetry(() => import('./pages/suburbs/SouthAustraliaPage'));
const AdelaidePage = lazyRetry(() => import('./pages/suburbs/AdelaidePage'));
const UnleyPage = lazyRetry(() => import('./pages/suburbs/UnleyPage'));
const NorwoodPage = lazyRetry(() => import('./pages/suburbs/NorwoodPage'));
const ProspectPage = lazyRetry(() => import('./pages/suburbs/ProspectPage'));
const GlenelgPage = lazyRetry(() => import('./pages/suburbs/GlenelgPage'));
const BrightonPage = lazyRetry(() => import('./pages/suburbs/BrightonPage'));
const BurnsidePage = lazyRetry(() => import('./pages/suburbs/BurnsidePage'));
const MitchamPage = lazyRetry(() => import('./pages/suburbs/MitchamPage'));
const HenleyBeachPage = lazyRetry(() => import('./pages/suburbs/HenleyBeachPage'));
const MardenPage = lazyRetry(() => import('./pages/suburbs/MardenPage'));
const FulhamPage = lazyRetry(() => import('./pages/suburbs/FulhamPage'));
const MagillPage = lazyRetry(() => import('./pages/suburbs/MagillPage'));
const PlymptonPage = lazyRetry(() => import('./pages/suburbs/PlymptonPage'));

// Help and Advice Pages - lazy loaded
const HelpIndexPage = lazyRetry(() => import('./pages/help/HelpIndexPage'));
const HelpCategoryPage = lazyRetry(() => import('./pages/help/HelpCategoryPage'));

// Help Article Pages - lazy loaded as a group
const CheapestLandSA = lazyRetry(() => import('./pages/help').then(m => ({ default: m.CheapestLandSA })));
const CheapestLandNow = lazyRetry(() => import('./pages/help').then(m => ({ default: m.CheapestLandNow })));
const AdelaideSuburbsBoom = lazyRetry(() => import('./pages/help').then(m => ({ default: m.AdelaideSuburbsBoom })));
const BuyLandSAGov = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BuyLandSAGov })));
const HouseAndLandMoney = lazyRetry(() => import('./pages/help').then(m => ({ default: m.HouseAndLandMoney })));
const BuildHouse600k = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BuildHouse600k })));
const HouseAndLandWorthIt = lazyRetry(() => import('./pages/help').then(m => ({ default: m.HouseAndLandWorthIt })));
const HousePricesFalling = lazyRetry(() => import('./pages/help').then(m => ({ default: m.HousePricesFalling })));
const BestHomeBuilderSA = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BestHomeBuilderSA })));
const CostToBuildAdelaide = lazyRetry(() => import('./pages/help').then(m => ({ default: m.CostToBuildAdelaide })));
const MostReliableBuilder = lazyRetry(() => import('./pages/help').then(m => ({ default: m.MostReliableBuilder })));
const HouseCostBuildAdelaide = lazyRetry(() => import('./pages/help').then(m => ({ default: m.HouseCostBuildAdelaide })));
const SuburbsBoomAdelaide = lazyRetry(() => import('./pages/help').then(m => ({ default: m.SuburbsBoomAdelaide })));
const CheaperBuildOrBuy = lazyRetry(() => import('./pages/help').then(m => ({ default: m.CheaperBuildOrBuy })));
const BuilderBestReputation = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BuilderBestReputation })));
const Top5Construction = lazyRetry(() => import('./pages/help').then(m => ({ default: m.Top5Construction })));
const MostExpensivePart = lazyRetry(() => import('./pages/help').then(m => ({ default: m.MostExpensivePart })));
const BestBuyOrBuildAdelaide = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BestBuyOrBuildAdelaide })));
const BestTimeToBuild = lazyRetry(() => import('./pages/help').then(m => ({ default: m.BestTimeToBuild })));

// Adelaide Flagship Page
const BuildNewHomeAdelaidePage = lazyRetry(() => import('./pages/adelaide/BuildNewHomeAdelaidePage'));

// Referral Page
const ReferralPage = lazyRetry(() => import('./pages/ReferralPage'));
const ShareReferralPage = lazyRetry(() => import('./pages/ShareReferralPage'));

// Area Education Pages
const AreasIndexPage = lazyRetry(() => import('./pages/areas/AreasIndexPage'));
const AreaPageTemplate = lazyRetry(() => import('./pages/areas/AreaPageTemplate'));

// 404 Page
const NotFoundPage = lazyRetry(() => import('./pages/NotFoundPage'));

// Private Pages (NOT linked anywhere - access by direct URL only)
const EquableFinanceCalculator = lazyRetry(() => import('./pages/private/EquableFinanceCalculator'));
const GSCDashboard = lazyRetry(() => import('./pages/private/GSCDashboard'));

// Builder Comparison Pages
const CompareIndexPage = lazyRetry(() => import('./pages/CompareIndexPage'));
const BuilderComparisonPage = lazyRetry(() => import('./pages/BuilderComparisonPage'));

// Estate/Development Pages
const EstatesIndexPage = lazyRetry(() => import('./pages/EstatesIndexPage'));
const EstatePage = lazyRetry(() => import('./pages/EstatePage'));

// Pillar guides - long-form cornerstone editorial pages
const BuilderSelectionChecklistPage = lazyRetry(() => import('./pages/BuilderSelectionChecklistPage'));
const HbcfAdelaideExplainerPage = lazyRetry(() => import('./pages/HbcfAdelaideExplainerPage'));
const PlansaApprovalGuidePage = lazyRetry(() => import('./pages/PlansaApprovalGuidePage'));
const DbiMelbourneExplainerPage = lazyRetry(() => import('./pages/vic/money-pages/DbiMelbourneExplainerPage'));
const VbaBuilderRegistrationMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/VbaBuilderRegistrationMelbournePage'));
const AustralianHouseByDecadePage = lazyRetry(() => import('./pages/AustralianHouseByDecadePage'));
const CostOfEverythingPage = lazyRetry(() => import('./pages/CostOfEverythingPage'));
const TradieSlangPage = lazyRetry(() => import('./pages/TradieSlangPage'));
const SuburbHomeDnaPage = lazyRetry(() => import('./pages/SuburbHomeDnaPage'));
const SuburbTimeMachinePage = lazyRetry(() => import('./pages/SuburbTimeMachinePage'));
const RentalRepairRulebookPage = lazyRetry(() => import('./pages/RentalRepairRulebookPage'));
const CouncilQuirksPage = lazyRetry(() => import('./pages/CouncilQuirksPage'));
const YourHouseDecodedPage = lazyRetry(() => import('./pages/YourHouseDecodedPage'));
const WouldThisPassTodayPage = lazyRetry(() => import('./pages/WouldThisPassTodayPage'));
const HomeProblemsPage = lazyRetry(() => import('./pages/HomeProblemsPage'));
const WhatTheProsSeePage = lazyRetry(() => import('./pages/WhatTheProsSeePage'));
const RegionalWordsPage = lazyRetry(() => import('./pages/RegionalWordsPage'));

// Display Home Guide
const DisplayHomeGuidePage = lazyRetry(() => import('./pages/DisplayHomeGuidePage'));

// Display Homes Adelaide (SEO Mega Guide)
const DisplayHomesAdelaide = lazyRetry(() => import('./pages/DisplayHomesAdelaide'));

// Display Home Checklist (Lead Magnet Tool)
const DisplayHomeChecklist = lazyRetry(() => import('./pages/DisplayHomeChecklist'));

// Builder Type SEO Pages
const BuilderTypePage = lazyRetry(() => import('./pages/BuilderTypePage'));

// Display Home Location SEO Pages
const DisplayHomeLocationPage = lazyRetry(() => import('./pages/DisplayHomeLocationPage'));

// Builder Type Comparison Pages
const BuilderTypeComparisonPage = lazyRetry(() => import('./pages/BuilderTypeComparisonPage'));

// Best Builder For Pages
const BestBuilderForPage = lazyRetry(() => import('./pages/BestBuilderForPage'));
const MatchedRoundupPage = lazyRetry(() => import('./pages/MatchedRoundupPage'));
const InteriorStylingHubPage = lazyRetry(() => import('./pages/InteriorStylingHubPage'));
const RenoFinishingPage = lazyRetry(() => import('./pages/RenoFinishingPage'));

// Regional Landing Pages
const RegionalLandingPage = lazyRetry(() => import('./pages/RegionalLandingPage'));

// Decision Comparison Pages
const DecisionComparisonPage = lazyRetry(() => import('./pages/DecisionComparisonPage'));

// Ultimate Building Guide
const UltimateGuidePage = lazyRetry(() => import('./pages/UltimateGuidePage'));
const MelbourneUltimateGuidePage = lazyRetry(() => import('./pages/MelbourneUltimateGuidePage'));

// FAQ Landing Page
const FaqLandingPage = lazyRetry(() => import('./pages/FaqLandingPage'));

// Market Update Page
const MarketUpdatePage = lazyRetry(() => import('./pages/MarketUpdatePage'));

// Editorial process / E-E-A-T anchor page
const EditorialProcessPage = lazyRetry(() => import('./pages/EditorialProcessPage'));

// How BuildPilot Works - ecosystem positioning anchor page
const HowBuildPilotWorksPage = lazyRetry(() => import('./pages/HowBuildPilotWorksPage'));

// Programmatic SEO landing pages (budget×region, type×region)
const ProgrammaticSeoPage = lazyRetry(() => import('./pages/ProgrammaticSeoPage'));

// Outdoor Living pillar pages (pool, verandah, deck, shed, super-pillar).
// Data-driven; one component powers all 5 slugs. See /data/outdoorPillarData.js
const OutdoorPillarPage = lazyRetry(() => import('./pages/OutdoorPillarPage'));
const AdelaideQuestionPage = lazyRetry(() => import('./pages/AdelaideQuestionPage'));
const MelbourneQuestionPage = lazyRetry(() => import('./pages/vic/MelbourneQuestionPage'));
const MelbourneSuburbPage = lazyRetry(() => import('./pages/vic/MelbourneSuburbPage'));
const MelbournePillarPage = lazyRetry(() => import('./pages/vic/MelbournePillarPage'));
const BuildingInMelbourneSuburbPage = lazyRetry(() => import('./pages/vic/BuildingInMelbourneSuburbPage'));
const BuildingInMelbourneHubPage = lazyRetry(() => import('./pages/vic/BuildingInMelbourneHubPage'));
const BuildingInGeelongHubPage = lazyRetry(() => import('./pages/vic/BuildingInGeelongHubPage'));
const BuildingInBallaratHubPage = lazyRetry(() => import('./pages/vic/BuildingInBallaratHubPage'));
const BuildingInBendigoHubPage = lazyRetry(() => import('./pages/vic/BuildingInBendigoHubPage'));
const BushfireBuildersMelbournePage = lazyRetry(() => import('./pages/vic/BushfireBuildersMelbournePage'));
const MelbourneBuildersDirectoryPage = lazyRetry(() => import('./pages/vic/MelbourneBuildersDirectoryPage'));
const BuilderComplaintsMelbournePage = lazyRetry(() => import('./pages/vic/BuilderComplaintsMelbournePage'));
const BuildingContractsMelbourneGuidePage = lazyRetry(() => import('./pages/vic/BuildingContractsMelbourneGuidePage'));
const BuildingTimelineVicGuidePage = lazyRetry(() => import('./pages/vic/BuildingTimelineVicGuidePage'));
const WarrantyAfterHandoverMelbournePage = lazyRetry(() => import('./pages/vic/WarrantyAfterHandoverMelbournePage'));
const BuildVsBuyMelbourneGuidePage = lazyRetry(() => import('./pages/vic/BuildVsBuyMelbourneGuidePage'));
const GrannyFlatRulesVicPage = lazyRetry(() => import('./pages/vic/GrannyFlatRulesVicPage'));
const PoolFencingRulesVicPage = lazyRetry(() => import('./pages/vic/PoolFencingRulesVicPage'));
const DualOccupancyMelbournePage = lazyRetry(() => import('./pages/vic/DualOccupancyMelbournePage'));
const BuildingSurveyorsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/BuildingSurveyorsMelbournePage'));
const BuildingLawyersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/BuildingLawyersMelbournePage'));
const ConveyancersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/ConveyancersMelbournePage'));
const EnergyAssessorsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/EnergyAssessorsMelbournePage'));
const BuildingDesignersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/BuildingDesignersMelbournePage'));
const FinanceBrokersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/FinanceBrokersMelbournePage'));
const InteriorDesignersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/InteriorDesignersMelbournePage'));
const LandSurveyorsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/LandSurveyorsMelbournePage'));
const QuantitySurveyorsMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/QuantitySurveyorsMelbournePage'));
const StructuralEngineersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/StructuralEngineersMelbournePage'));
const TownPlannersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/TownPlannersMelbournePage'));
const PrivateCertifiersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/PrivateCertifiersMelbournePage'));
const ReliableBuildersMelbournePage = lazyRetry(() => import('./pages/vic/money-pages/ReliableBuildersMelbournePage'));
const MelbourneEstatePage = lazyRetry(() => import('./pages/vic/MelbourneEstatePage'));
const VicGrantDetailPage = lazyRetry(() => import('./pages/vic/VicGrantDetailPage'));
const VicAllGuidesPage = lazyRetry(() => import('./pages/vic/VicAllGuidesPage'));
const StateWaitlistPage = lazyRetry(() => import('./pages/StateWaitlistPage'));

// Outdoor Living suburb pages (Sprint 1, 2026-02-28) - 6 suburb-variant
// pages targeting local-search intent. Deliberately shorter than the
// pillar to avoid cannibalisation. See /data/outdoorSuburbPagesData.js
const OutdoorSuburbPage = lazyRetry(() => import('./pages/OutdoorSuburbPage'));

// Pool Fencing Rules SA - regulatory content (Sprint 1, 2026-02-28)
const PoolFencingRulesSAPage = lazyRetry(() => import('./pages/PoolFencingRulesSAPage'));

// Lead magnet pages (3 magnets share one component)
const LeadMagnetPage = lazyRetry(() => import('./pages/LeadMagnetPage'));

// Case studies (data-driven; one component per slug)
const CaseStudyPage = lazyRetry(() => import('./pages/CaseStudyPage'));
const CaseStudiesIndexPage = lazyRetry(() => import('./pages/CaseStudiesIndexPage'));

// Project Type Pages
const ProjectTypePage = lazyRetry(() => import('./pages/ProjectTypePage'));

// Council Guide Pages
const CouncilGuidePage = lazyRetry(() => import('./pages/CouncilGuidePage'));
const CouncilApprovalsAdelaideGuidePage = lazyRetry(() => import('./pages/CouncilApprovalsAdelaideGuidePage'));
const PrivateCertificationSAGuidePage = lazyRetry(() => import('./pages/PrivateCertificationSAGuidePage'));
const BlockCheckHubPage = lazyRetry(() => import('./pages/BlockCheckHubPage'));
const BlockCheckPillarPage = lazyRetry(() => import('./pages/BlockCheckPillarPage'));
const VerifyBuilderHubPage = lazyRetry(() => import('./pages/VerifyBuilderHubPage'));
const VerifyBuilderPillarPage = lazyRetry(() => import('./pages/VerifyBuilderPillarPage'));
const ClimateDesignHubPage = lazyRetry(() => import('./pages/ClimateDesignHubPage'));
const ClimateDesignPillarPage = lazyRetry(() => import('./pages/ClimateDesignPillarPage'));
const HomeLayoutHubPage = lazyRetry(() => import('./pages/HomeLayoutHubPage'));
const HomeLayoutPillarPage = lazyRetry(() => import('./pages/HomeLayoutPillarPage'));
const BuildingContractsAdelaideGuidePage = lazyRetry(() => import('./pages/BuildingContractsAdelaideGuidePage'));
const PropertyInvestorsAdelaidePage = lazyRetry(() => import('./pages/PropertyInvestorsAdelaidePage'));
const FixedPriceVsCostPlusAdelaidePage = lazyRetry(() => import('./pages/FixedPriceVsCostPlusAdelaidePage'));
const InfillVsGreenfieldLandAdelaidePage = lazyRetry(() => import('./pages/InfillVsGreenfieldLandAdelaidePage'));
const DifficultBlockBuilderAdelaidePage = lazyRetry(() => import('./pages/DifficultBlockBuilderAdelaidePage'));
const IOwnLandAlreadyAdelaidePage = lazyRetry(() => import('./pages/IOwnLandAlreadyAdelaidePage'));
const FirstHomeGuaranteeSchemeAdelaidePage = lazyRetry(() => import('./pages/FirstHomeGuaranteeSchemeAdelaidePage'));
const FirstHomeOwnerGrantSAAdelaidePage = lazyRetry(() => import('./pages/FirstHomeOwnerGrantSAAdelaidePage'));
const FirstHomeSuperSaverAdelaidePage = lazyRetry(() => import('./pages/FirstHomeSuperSaverAdelaidePage'));
const HomeStartFinanceAdelaidePage = lazyRetry(() => import('./pages/HomeStartFinanceAdelaidePage'));
const StampDutyFirstHomeBuyerSAAdelaidePage = lazyRetry(() => import('./pages/StampDutyFirstHomeBuyerSAAdelaidePage'));
const FamilyHomeGuaranteeAdelaidePage = lazyRetry(() => import('./pages/FamilyHomeGuaranteeAdelaidePage'));
const HomeDesignsTaxonomyPage = lazyRetry(() => import('./pages/HomeDesignsTaxonomyPage'));
const BuilderVsPage = lazyRetry(() => import('./pages/BuilderVsPage'));
const MatcherRoute = lazyRetry(() => import('./pages/MatcherRoute'));
const AudienceMatcherRoute = lazyRetry(() => import('./pages/AudienceMatcherRoute'));
const StartBuildingPage = lazyRetry(() => import('./pages/StartBuildingPage'));
const NewHomeAdvisorsPage = lazyRetry(() => import('./pages/NewHomeAdvisorsPage'));
const BuildingTimelineAdelaideGuidePage = lazyRetry(() => import('./pages/BuildingTimelineAdelaideGuidePage'));
const WarrantyAndAfterHandoverAdelaidePage = lazyRetry(() => import('./pages/WarrantyAndAfterHandoverAdelaidePage'));
const AdelaideBuildingMarketReport = lazyRetry(() => import('./pages/AdelaideBuildingMarketReport'));
const BuildVsBuyAdelaideGuidePage = lazyRetry(() => import('./pages/BuildVsBuyAdelaideGuidePage'));

// Problem Pages
const ProblemPage = lazyRetry(() => import('./pages/ProblemPage'));

// Seasonal Content Pages
const SeasonalContentPage = lazyRetry(() => import('./pages/SeasonalContentPage'));

// Interactive Tools
const BuilderQuizPage = lazyRetry(() => import('./pages/BuilderQuizPage'));
const ReadyToBuildPage = lazyRetry(() => import('./pages/ReadyToBuildPage'));

// House & Land Content Pages
const HouseAndLandPage = lazyRetry(() => import('./pages/HouseAndLandPage'));
const HLComparisonPage = lazyRetry(() => import('./pages/HLComparisonPage'));
const HouseAndLandVsBuyingSeparatelyPage = lazyRetry(() => import('./pages/HouseAndLandVsBuyingSeparatelyPage'));
const BuildDecisionsHubPage = lazyRetry(() => import('./pages/BuildDecisionsHubPage'));
const ResearchCompareHubPage = lazyRetry(() => import('./pages/ResearchCompareHubPage'));
const ResearchDirectoryPage = lazyRetry(() => import('./pages/ResearchDirectoryPage'));
const CostIndexPage = lazyRetry(() => import('./pages/CostIndexPage'));
const ReportsHubPage = lazyRetry(() => import('./pages/ReportsHubPage'));
const BuildingCostReport2026Page = lazyRetry(() => import('./pages/BuildingCostReport2026Page'));
const BudgetRealityPage = lazyRetry(() => import('./pages/BudgetRealityPage'));
const HLWarningPage = lazyRetry(() => import('./pages/HLWarningPage'));
const FirstHomeBuyerHLPage = lazyRetry(() => import('./pages/FirstHomeBuyerHLPage'));

// House & Land Budget & Region Pages
const HLBudgetPage = lazyRetry(() => import('./pages/HLBudgetPage'));

// New Builder Category Pages (targeting search terms)
const SmallHomeBuildersPage = lazyRetry(() => import('./pages/SmallHomeBuildersPage'));
const TransportableHomesPage = lazyRetry(() => import('./pages/TransportableHomesPage'));
const CountryBuildersPage = lazyRetry(() => import('./pages/CountryBuildersPage'));
const BedroomPricingPage = lazyRetry(() => import('./pages/BedroomPricingPage'));

// New SEO Pages (targeting "People Also Ask" questions)
const ReliableBuildersPage = lazyRetry(() => import('./pages/ReliableBuildersPage'));
const BuildVsBuyPage = lazyRetry(() => import('./pages/BuildVsBuyPage'));
const GrowthSuburbsPage = lazyRetry(() => import('./pages/GrowthSuburbsPage'));
const AwardsPage = lazyRetry(() => import('./pages/AwardsPage'));
const MediaPage = lazyRetry(() => import('./pages/MediaPage'));
const MediaReleasePage = lazyRetry(() => import('./pages/MediaReleasePage'));
const PartnersPage = lazyRetry(() => import('./pages/PartnersPage'));
const PayLockerHomeownersPage = lazyRetry(() => import('./pages/PayLockerHomeownersPage'));
const PayLockerForBusinessPage = lazyRetry(() => import('./pages/PayLockerForBusinessPage'));

// Home Designs Pages
const HomeDesignsPage = lazyRetry(() => import('./pages/HomeDesignsPage'));
const BuilderDesignsPage = lazyRetry(() => import('./pages/BuilderDesignsPage'));
const PlanDetailPage = lazyRetry(() => import('./pages/PlanDetailPage'));
const ComparePage = lazyRetry(() => import('./pages/ComparePage'));
const ComparingFourBedDesignsPage = lazyRetry(() => import('./pages/ComparingFourBedDesignsPage'));
const DesignsFor12mBlockPage = lazyRetry(() => import('./pages/DesignsFor12mBlockPage'));
const ComparingBuilderInclusionsPage = lazyRetry(() => import('./pages/ComparingBuilderInclusionsPage'));
const DisplayHomeDirectoryPage = lazyRetry(() => import('./pages/DisplayHomeDirectoryPage'));
const DisplayHomesHubPage = lazyRetry(() => import('./pages/DisplayHomesHubPage'));
const AdelaideDataPage = lazyRetry(() => import('./pages/AdelaideDataPage'));

/**
 * GlobalQuickAddFab, renders the Quick Actions FAB everywhere on the
 * website when the user is logged in.
 *
 * - Mounted at the App root so the FAB is reachable from marketing
 *   pages too (Builders, Designs, Suburbs, etc.), not just /workspace/*.
 * - Hidden when logged-out (no point showing it to anonymous visitors).
 * - Hidden on full-bleed workspace flows (onboarding, paywall takeover,
 *   find-a-builder, design detail) so the wizard owns the viewport.
 * - Wrapped in `.bp-workspace` so it picks up the workspace FAB styling
 *   (which is scoped to that class in workspace.css).
 */
const GlobalPageViewTracker = () => {
  // 2026-02-15 Slice A - single global page-view tracker.
  // Fires `page_view` to user_events on every route change.
  // Anonymous: tracked with session_id only. Logged-in: with user_id.
  const { pathname } = useLocation();
  const { user } = useMarketingAuth();
  React.useEffect(() => {
    if (!pathname) return;
    // Skip noisy admin / health surfaces
    if (pathname.startsWith('/admin') || pathname.startsWith('/__')) return;
    try {
      // eslint-disable-next-line global-require
      const { trackEvent } = require('./lib/trackEvent');
      trackEvent('page_view', { path: pathname }, { user });
    } catch { /* swallow */ }
  }, [pathname, user?.id]);
  return null;
};

const GlobalQuickAddFab = () => {
  const { isLoggedIn } = useMarketingAuth();
  const { pathname } = useLocation();
  const isFullBleed =
    pathname.startsWith('/workspace/onboarding') ||
    pathname.startsWith('/workspace/legal-consent') ||
    pathname.startsWith('/workspace/phone-verification') ||
    pathname.startsWith('/workspace/land-status-gate') ||
    pathname.startsWith('/workspace/house-land-offer-gate') ||
    pathname.startsWith('/workspace/fhb-gate') ||
    pathname.startsWith('/workspace/tools/find-a-builder') ||
    pathname.startsWith('/workspace/login') ||
    pathname.startsWith('/workspace/signup');
  // Plan-detail pages have their own bottom-of-page primary CTAs
  // (Add to My Shortlist + Book One on One Support). The centre-bottom
  // FAB sat on top of the full-width Shortlist button and made it
  // unreachable, so we hide it on those routes.
  const isPlanDetail =
    /^\/home-designs\/[^/]+\/[^/]+/.test(pathname) ||
    /^\/designs\/[^/]+$/.test(pathname);
  if (!isLoggedIn || isFullBleed || isPlanDetail) return null;
  return (
    <div className="bp-workspace bp-global-fab-host">
      <QuickAddFab />
    </div>
  );
};


function App() {
  // Remove any platform badges on mount
  useEffect(() => {
    // Clean up any injected badges on page load
    const removeBadges = () => {
      const selectors = [
        '[data-testid*="badge"]',
        '[class*="badge"][style*="fixed"]',
        'div[style*="position: fixed"][style*="bottom"][style*="right"]',
      ];
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          if (el && !el.closest('#root')) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.remove();
          }
        });
      });
    };
    
    removeBadges();
    const interval = setInterval(removeBadges, 1000);
    return () => clearInterval(interval);
  }, []);

  // Site-wide App Store / Google Play download-click tracking (GA4 via GTM).
  useEffect(() => {
    initAppDownloadTracking();
  }, []);

  // State detection + first-visit location modal are handled by <LocationGate />
  // (mounted inside the router so it can read the current path). On neutral
  // URLs it shows the "Where are you building?" modal; on deep pages it runs
  // the silent IP geo-detect. Both respect a prior manual pick.

  // SA-only gating flag, mirrored onto <html data-geo> so pure-CSS gates
  // (saGeo.css) can hide every tel: + One on One Support link site-wide.
  // Fail-closed: index.html ships data-geo="non-sa" so nothing SA-only
  // flashes before React resolves the visitor's state.
  const isSA = useIsSA();
  useEffect(() => {
    document.documentElement.setAttribute('data-geo', isSA ? 'sa' : 'non-sa');
  }, [isSA]);

  return (
    <HelmetProvider>
    <OrganizationSchema />
    <div className="App">
      <GlassFilterDefs />
      <ContactModalProvider>
        <AuthModalProvider>
        <BrowserRouter>
          <AuthProvider>
          <FavouritesProvider>
          <CompareProvider>
          <CompareTrayProvider>
          <ScrollToTop />
          <LocationGate />
          <SAContactGuard />
          <MetaDeduper />
          <FavouritesSidebar />
          <FavouritesFab />
          <CompareBar />
          <CompareTrayBar />
          <StageAwareToolStrip />
          <GlobalPageViewTracker />
          <GlobalQuickAddFab />
          <SmartTriggers />
          <DeepLinkMatcher />
          <StateContextGate />
          <AdelaideUrlHeadsUpStrip />
          <ReciprocalGeoAlternate />
          <LinkPrefetcher />
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Home */}
          <Route path="/" element={<HomePage />} />

          {/* Life-stage lanes - 18 through 60+ home journey hubs */}
          <Route path="/where-are-you-at" element={<LifeStageIndexPage />} />
          <Route path="/where-are-you-at/:stage" element={<LifeStageHubPage />} />

          {/* My BuildPilot - anonymous-friendly saved-items hub */}
          <Route path="/my-buildpilot" element={<MyBuildPilotPage />} />

          {/* SEO landing pages - programmatic life-stage query capture.
              Each slug maps to a data file in
              /app/frontend/src/data/seoLandingPages/ - see
              /app/frontend/src/pages/seoLandingPages.jsx for the full
              index. Rendered via one shared SeoLandingPage template
              so scaling to 30+ pages is data-entry, not code. */}
          {SEO_LANDING_SLUGS.map((slug) => (
            <Route
              key={slug}
              path={`/${slug}`}
              element={<SeoLandingPageBySlug slug={slug} />}
            />
          ))}

          {/* Advertise / Featured Business landing */}
          <Route path="/advertise" element={<AdvertisePage />} />

          {/* Short SEO slug redirects - common bare URLs often hit by
              external links / search snippets. Forward to the canonical
              long-form route so we never 404 on a guessable URL. */}
          <Route path="/house-and-land" element={<Navigate to="/house-and-land-adelaide" replace />} />
          <Route path="/land" element={<Navigate to="/land-for-sale-adelaide" replace />} />
          <Route path="/building-disputes" element={<Navigate to="/help/building-disputes" replace />} />
          <Route path="/land-for-sale" element={<Navigate to="/land-for-sale-adelaide" replace />} />
          <Route path="/find-a-builder" element={<Navigate to="/find/builder" replace />} />
          <Route path="/about" element={<Navigate to="/about/editorial-process" replace />} />
          <Route path="/pricing" element={<Navigate to="/adelaide-build-cost-report-2026" replace />} />
          <Route path="/faq" element={<Navigate to="/faqs" replace />} />
          {/* 2026-02 - bare slugs guessed by AI crawlers / external links. */}
          <Route path="/builders-adelaide" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/floor-plans-library" element={<Navigate to="/home-designs" replace />} />
          <Route path="/floor-plans" element={<Navigate to="/home-designs" replace />} />
          <Route path="/adelaide-suburbs" element={<Navigate to="/suburbs" replace />} />
          <Route path="/adelaide-suburbs/:suburbSlug" element={<AdelaideSuburbRedirect />} />
          <Route path="/choosing-a-block-of-land" element={<Navigate to="/choosing-land" replace />} />
          <Route path="/find/supplier/:slug" element={<FindSupplierRedirect />} />

          {/* Sprint 1 - Platform Hubs */}
          <Route path="/builders" element={<BuildersHub />} />
          {/* P0-3: fold the observed mixed-case variant onto the canonical
              lowercase URL. Narrow, single-path correction - no broad
              lowercase redirect rule. */}
          <Route path="/Builders" element={<Navigate to="/builders" replace />} />
          <Route path="/builders/:slug" element={<BuilderSpotlight />} />

          {/* SEO programmatic building-type surface: 13 hubs + 390 suburb
              matrix + 8 comparison pages. Order matters - keep the
              /build/compare/:pair BEFORE /build/:type/:suburb so the
              literal "compare" segment routes to the comparison page
              instead of being mis-matched as a suburb. */}
          <Route path="/build/compare/:pair" element={<BuildingTypeComparisonPage />} />
          <Route path="/build/:type/melbourne" element={<BuildingTypeSeoHubPage metro="Melbourne" />} />
          <Route path="/build/:type/geelong" element={<BuildingTypeSeoHubPage metro="Geelong" />} />
          <Route path="/build/:type/:suburb" element={<BuildingTypeInSuburbPage />} />
          <Route path="/build/:type" element={<BuildingTypeSeoHubPage />} />
          <Route path="/designs" element={<DesignsHub />} />
          <Route path="/inspiration" element={<InspirationHub />} />
          <Route path="/inspiration/project/:slug" element={<InspirationProjectPage />} />
          <Route path="/build-types" element={<BuildTypesIndex />} />
          <Route path="/build-type-design/:slug" element={<BuildTypeDesignPage />} />
          <Route path="/suburbs" element={<SuburbsHub />} />
          {/* /guides is served by GuidesIndexPage below (3-lane reorg iter137) */}
          <Route path="/journeys" element={<HubPage slug="journeys" />} />
          <Route path="/journeys/:slug" element={<JourneyPage />} />
          <Route path="/one-on-one-support" element={<OneOnOneSupportPage />} />
          <Route path="/watch" element={<WatchHubPage />} />
          <Route path="/watch/:slug" element={<WatchEpisodePage />} />
          <Route path="/questions" element={<QuestionsIndexPage />} />
          <Route path="/questions/:slug" element={<QuestionPage />} />
          <Route path="/community" element={<CommunityPage />} />

          {/* Suppliers Hub */}
          <Route path="/suppliers" element={<SuppliersHub />} />
          <Route path="/house-and-land-adelaide" element={<><SaVicHreflangPair selfPath="/house-and-land-adelaide" selfState="SA" siblingPath="/house-and-land-melbourne" /><PackagesHubPage /></>} />
          <Route path="/house-and-land-adelaide/filter/:filterSlug" element={<PackageFilterPage />} />
          <Route path="/house-and-land-adelaide/northern-adelaide" element={<VicRegionHubPage regionKey="northern-adelaide" />} />
          <Route path="/house-and-land-adelaide/southern-adelaide" element={<VicRegionHubPage regionKey="southern-adelaide" />} />
          <Route path="/house-and-land-adelaide/north-east-adelaide" element={<VicRegionHubPage regionKey="north-east-adelaide" />} />
          <Route path="/house-and-land-adelaide/adelaide-hills" element={<VicRegionHubPage regionKey="adelaide-hills" />} />
          <Route path="/house-and-land-adelaide/regional-sa" element={<VicRegionHubPage regionKey="regional-sa" />} />
          <Route path="/house-and-land-adelaide/:suburbSlug/filter/:filterSlug" element={<SuburbFilterPage />} />
          <Route path="/house-and-land-adelaide/:suburbSlug" element={<PackageSuburbPage />} />
          <Route path="/house-and-land-adelaide/:suburbSlug/:listingSlug" element={<PackageDetailPage />} />
          {/* Parked-VIC mirror route. Reuses PackageSuburbPage in
              `parked="vic"` mode which renders `noindex, nofollow` and
              a Coming-Soon UI. See parkedVicSuburbContent.js for the
              slug allowlist and rationale. */}
          <Route path="/vic/house-and-land/:suburbSlug" element={<VicHnlSuburbRedirect />} />
          {/* VIC content pillars, indexable per BuildPilot curator positioning. */}
          <Route path="/vic" element={<VicHubPage />} />
          <Route path="/vic/grants" element={<VicGrantsPage />} />
          {/* Dedicated grant detail pages (Phase 2, 2026-03-05) - one per grant so
              each ranks for its specific query without competing with the others. */}
          <Route path="/vic/first-home-owner-grant-vic" element={<VicGrantDetailPage />} />
          <Route path="/vic/homebuyer-fund-vic" element={<VicGrantDetailPage />} />
          <Route path="/vic/stamp-duty-concession-vic" element={<VicGrantDetailPage />} />
          <Route path="/vic/first-home-guarantee-vic" element={<VicGrantDetailPage />} />
          <Route path="/vic/first-home-super-saver-vic" element={<VicGrantDetailPage />} />
          {/* Flat -melbourne grant slug twins - mirror the Adelaide redirect
              pattern (e.g. /fhog-adelaide -> /first-home-buyer-adelaide) so the
              Melbourne slug family has the same coverage. Each 301s to the
              canonical /vic grant detail page. */}
          <Route path="/first-home-owner-grant-melbourne" element={<Navigate to="/vic/first-home-owner-grant-vic" replace />} />
          <Route path="/fhog-melbourne" element={<Navigate to="/vic/first-home-owner-grant-vic" replace />} />
          <Route path="/first-home-super-saver-melbourne" element={<Navigate to="/vic/first-home-super-saver-vic" replace />} />
          <Route path="/first-home-guarantee-melbourne" element={<Navigate to="/vic/first-home-guarantee-vic" replace />} />
          <Route path="/first-home-guarantee-scheme-melbourne" element={<Navigate to="/vic/first-home-guarantee-vic" replace />} />
          <Route path="/stamp-duty-first-home-buyer-vic-melbourne" element={<Navigate to="/vic/stamp-duty-concession-vic" replace />} />
          <Route path="/homebuyer-fund-melbourne" element={<Navigate to="/vic/homebuyer-fund-vic" replace />} />
          <Route path="/building-costs-melbourne-2026" element={<Navigate to="/melbourne-building-costs" replace />} />
          <Route path="/building-costs-melbourne" element={<Navigate to="/melbourne-building-costs" replace />} />
          <Route path="/vic/all-guides" element={<VicAllGuidesPage />} />
          <Route path="/vic/building-in/:councilSlug" element={<VicCouncilGuidePage />} />
          {/* Melbourne SEO money pages - Sprint 1. Unique URL, unique
              H1, unique meta, unique body per URL so Google reads
              them as legitimate geo-localised pages, not duplicates
              of the Adelaide siblings. */}
          <Route path="/first-home-buyer-melbourne" element={<FirstHomeBuyerMelbournePage />} />
          <Route path="/melbourne-build-cost-report-2026" element={<MelbourneBuildCostReport2026Page />} />
          <Route path="/home-builders-melbourne" element={<HomeBuildersMelbournePage />} />
          <Route path="/knockdown-rebuild-melbourne" element={<KnockdownRebuildMelbournePage />} />
          <Route path="/house-and-land-melbourne" element={<><SaVicHreflangPair selfPath="/house-and-land-melbourne" selfState="VIC" siblingPath="/house-and-land-adelaide" /><HouseAndLandMelbournePage /></>} />
          <Route path="/what-can-you-build-for-500k-melbourne" element={<><SaVicHreflangPair selfPath="/what-can-you-build-for-500k-melbourne" selfState="VIC" siblingPath="/what-can-you-build-for-500k-adelaide" /><WhatCanYouBuildFor500kMelbournePage /></>} />
          <Route path="/what-can-you-build-for-650k-melbourne" element={<><SaVicHreflangPair selfPath="/what-can-you-build-for-650k-melbourne" selfState="VIC" siblingPath="/what-can-you-build-for-650k-adelaide" /><WhatCanYouBuildFor650kMelbournePage /></>} />
          <Route path="/what-can-you-build-for-850k-melbourne" element={<><SaVicHreflangPair selfPath="/what-can-you-build-for-850k-melbourne" selfState="VIC" siblingPath="/what-can-you-build-for-850k-adelaide" /><WhatCanYouBuildFor850kMelbournePage /></>} />
          <Route path="/fixed-price-vs-cost-plus-melbourne" element={<><SaVicHreflangPair selfPath="/fixed-price-vs-cost-plus-melbourne" selfState="VIC" siblingPath="/fixed-price-vs-cost-plus-adelaide" /><FixedPriceVsCostPlusMelbournePage /></>} />
          <Route path="/volume-vs-custom-builder-melbourne" element={<><SaVicHreflangPair selfPath="/volume-vs-custom-builder-melbourne" selfState="VIC" siblingPath="/volume-vs-custom-builder-adelaide" /><VolumeVsCustomBuilderMelbournePage /></>} />
          <Route path="/choosing-land-melbourne" element={<><SaVicHreflangPair selfPath="/choosing-land-melbourne" selfState="VIC" siblingPath="/choosing-land-adelaide" /><ChoosingLandMelbournePage /></>} />
          {/* Guides Round 2 (VIC-only, 2026-06) - no SA sibling, own canonical */}
          <Route path="/owner-builder-melbourne" element={<OwnerBuilderMelbournePage />} />
          <Route path="/site-costs-melbourne" element={<SiteCostsMelbournePage />} />
          <Route path="/7-star-energy-melbourne" element={<SevenStarEnergyMelbournePage />} />
          {/* Melbourne growth-corridor hub pages (VIC, 2026-06) - roll up suburb + guide pages */}
          <Route path="/home-builders-wyndham" element={<MelbourneCorridorHubPage hub="wyndham" />} />
          <Route path="/home-builders-melton" element={<MelbourneCorridorHubPage hub="melton" />} />
          <Route path="/home-builders-casey" element={<MelbourneCorridorHubPage hub="casey" />} />
          <Route path="/home-builders-hume" element={<MelbourneCorridorHubPage hub="hume" />} />
          <Route path="/home-builders-whittlesea" element={<MelbourneCorridorHubPage hub="whittlesea" />} />
          <Route path="/home-builders-cardinia" element={<MelbourneCorridorHubPage hub="cardinia" />} />
          <Route path="/melbourne-growth-corridors" element={<MelbourneCorridorsOverviewPage />} />
          <Route path="/how-to-build-a-house-melbourne" element={<Navigate to="/complete-guide-building-melbourne" replace />} />
          <Route path="/house-and-land-melbourne/filter/:filterSlug" element={<PackageFilterPage market="vic" />} />
          <Route path="/house-and-land-melbourne/melbourne" element={<VicRegionHubPage regionKey="melbourne" />} />
          <Route path="/house-and-land-melbourne/geelong" element={<VicRegionHubPage regionKey="geelong" />} />
          <Route path="/house-and-land-melbourne/regional-vic" element={<VicRegionHubPage regionKey="regional-vic" />} />
          <Route path="/house-and-land-melbourne/:suburbSlug/:listingSlug" element={<PackageDetailPage market="vic" />} />
          <Route path="/house-and-land-melbourne/:suburbSlug/filter/:filterSlug" element={<SuburbFilterPage market="vic" />} />
          <Route path="/house-and-land-melbourne/:suburbSlug" element={<PackageSuburbPage parked="vic" />} />
          {/* Melbourne H&L budget-tier guides (VIC namespace, 2026-06) */}
          <Route path="/house-and-land-packages-melbourne-under-500k" element={<HLBudgetMelbournePage />} />
          <Route path="/house-and-land-packages-melbourne-under-600k" element={<HLBudgetMelbournePage />} />
          <Route path="/house-and-land-packages-melbourne-under-700k" element={<HLBudgetMelbournePage />} />
          <Route path="/best-new-home-builders/:suburb" element={<BestNewHomeBuildersVicPage />} />
          {/* Melbourne "building a new home in {suburb}" cluster (VIC, 2026-06).
              Building-journey intent (council permits, corridor site costs,
              KDR, grants, live H&L) - distinct from /best-new-home-builders
              (builder selection) which it cross-links to. VIC-only, own
              namespace, own canonical - does NOT touch SA /building-in/. */}
          <Route path="/building-in-melbourne" element={<BuildingInMelbourneHubPage />} />
          <Route path="/building-in-melbourne/estate/:slug" element={<MelbourneEstatePage />} />
          <Route path="/building-in-melbourne/:suburb" element={<BuildingInMelbourneSuburbPage metro="Melbourne" />} />
          {/* Geelong cluster - same metro-aware template, Geelong-branded, own canonical (2026-06) */}
          <Route path="/building-in-geelong" element={<BuildingInGeelongHubPage />} />
          <Route path="/building-in-geelong/:suburb" element={<BuildingInMelbourneSuburbPage metro="Geelong" />} />
          {/* Regional VIC hubs (2026-06, separate namespaces, protect Melbourne/Adelaide) */}
          <Route path="/building-in-ballarat" element={<BuildingInBallaratHubPage />} />
          <Route path="/building-in-ballarat/:suburb" element={<BuildingInMelbourneSuburbPage metro="Ballarat" />} />
          <Route path="/building-in-bendigo" element={<BuildingInBendigoHubPage />} />
          <Route path="/building-in-bendigo/:suburb" element={<BuildingInMelbourneSuburbPage metro="Bendigo" />} />
          {/* VIC bushfire cluster + Melbourne builders directory (2026-06) */}
          <Route path="/bushfire-builders-melbourne" element={<BushfireBuildersMelbournePage />} />
          <Route path="/bushfire-builders-melbourne/bal-ratings-vic-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-melbourne/bmo-planning-permit-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-melbourne/as3959-construction-guide" element={<ClusterHowToPage />} />
          <Route path="/melbourne-builders" element={<MelbourneBuildersDirectoryPage />} />
          <Route path="/builder-complaints-melbourne" element={<BuilderComplaintsMelbournePage />} />
          <Route path="/vba-disputes-melbourne" element={<Navigate to="/builder-complaints-melbourne" replace />} />
          {/* VIC process & rules content pack (2026-06, partner-free public-data guides) */}
          <Route path="/building-contracts-melbourne-guide" element={<BuildingContractsMelbourneGuidePage />} />
          <Route path="/building-timeline-vic-guide" element={<BuildingTimelineVicGuidePage />} />
          <Route path="/warranty-and-after-handover-melbourne" element={<WarrantyAfterHandoverMelbournePage />} />
          <Route path="/build-vs-buy-melbourne-guide" element={<BuildVsBuyMelbourneGuidePage />} />
          <Route path="/granny-flat-rules-vic" element={<GrannyFlatRulesVicPage />} />
          <Route path="/pool-fencing-rules-vic" element={<PoolFencingRulesVicPage />} />
          {/* VIC dual occupancy / duplex cluster (2026-06, partner-free) */}
          <Route path="/dual-occupancy-melbourne" element={<DualOccupancyMelbournePage />} />
          <Route path="/dual-occupancy-melbourne/planning-permit-rescode-guide" element={<ClusterHowToPage />} />
          <Route path="/dual-occupancy-melbourne/garden-area-rescode-standards-guide" element={<ClusterHowToPage />} />
          <Route path="/dual-occupancy-melbourne/subdivision-titles-guide" element={<ClusterHowToPage />} />
          <Route path="/dual-occupancy-melbourne/feasibility-finance-guide" element={<ClusterHowToPage />} />
          <Route path="/dual-occupancy-melbourne/party-wall-detailing-guide" element={<ClusterHowToPage />} />
          {/* VIC pro-services pages (2026-06, partner-free, mirror SA consultant cluster) */}
          <Route path="/building-surveyors-melbourne" element={<BuildingSurveyorsMelbournePage />} />
          <Route path="/building-lawyers-melbourne" element={<BuildingLawyersMelbournePage />} />
          <Route path="/conveyancers-melbourne" element={<ConveyancersMelbournePage />} />
          <Route path="/energy-assessors-melbourne" element={<EnergyAssessorsMelbournePage />} />
          {/* Melbourne parity batch 1 - professional services + best/top directory */}
          <Route path="/building-designers-melbourne" element={<BuildingDesignersMelbournePage />} />
          <Route path="/finance-brokers-melbourne" element={<FinanceBrokersMelbournePage />} />
          <Route path="/interior-designers-melbourne" element={<InteriorDesignersMelbournePage />} />
          <Route path="/land-surveyors-melbourne" element={<LandSurveyorsMelbournePage />} />
          <Route path="/quantity-surveyors-melbourne" element={<QuantitySurveyorsMelbournePage />} />
          <Route path="/structural-engineers-melbourne" element={<StructuralEngineersMelbournePage />} />
          <Route path="/town-planners-melbourne" element={<TownPlannersMelbournePage />} />
          <Route path="/private-certifiers-melbourne" element={<PrivateCertifiersMelbournePage />} />
          <Route path="/reliable-builders-melbourne" element={<ReliableBuildersMelbournePage />} />
          <Route path="/most-reliable-builders-melbourne" element={<Navigate to="/reliable-builders-melbourne" replace />} />
          <Route path="/trustworthy-builders-melbourne" element={<Navigate to="/reliable-builders-melbourne" replace />} />
          <Route path="/best-home-builders-melbourne" element={<Navigate to="/home-builders-melbourne" replace />} />
          <Route path="/top-10-home-builders-melbourne" element={<Navigate to="/home-builders-melbourne" replace />} />
          <Route path="/top-5-home-builders-melbourne" element={<Navigate to="/home-builders-melbourne" replace />} />
          <Route path="/local-builders-melbourne" element={<Navigate to="/home-builders-melbourne" replace />} />
          <Route path="/new-homes-melbourne" element={<Navigate to="/new-home-builders-melbourne" replace />} />
          <Route path="/compare-home-builders-melbourne" element={<Navigate to="/melbourne-home-builder-types" replace />} />
          {/* Sprint 2 - Hub Network */}
          <Route path="/adelaide-vs-melbourne-build" element={<AdelaideVsMelbourneBuildPage />} />
          <Route path="/adelaide-vs-melbourne-cost-comparator" element={<AdelaideVsMelbourneComparatorPage />} />
          <Route path="/best-suburbs-to-build-melbourne" element={<BestSuburbsToBuildMelbournePage />} />
          <Route path="/melbourne-building-costs" element={<MelbourneBuildingCostsPage />} />
          {/* Sprint 3 - Long tail */}
          <Route path="/architects-melbourne" element={<ArchitectsMelbournePage />} />
          <Route path="/building-inspectors-melbourne" element={<BuildingInspectorsMelbournePage />} />
          {/* VIC Phase A hubs (2026-03-05) - final 5 hub pages
              completing SA-parity for the Melbourne SEO surface.
              Informational-only per locked VIC content rule:
              no active Melbourne builder brand names surface here. */}
          <Route path="/melbourne-display-home-directory" element={<MelbourneDisplayHomesPage />} />
          <Route path="/growth-suburbs-melbourne" element={<MelbourneGrowthSuburbsPage />} />
          <Route path="/which-suburbs-will-boom-melbourne" element={<Navigate to="/growth-suburbs-melbourne" replace />} />
          <Route path="/melbourne-home-builder-types" element={<MelbourneHomeBuilderTypesPage />} />
          <Route path="/how-to-choose-a-home-builder-melbourne" element={<HowToChooseHomeBuilderMelbournePage />} />
          <Route path="/melbourne-building-market-report" element={<MelbourneBuildingMarketReport />} />
          <Route path="/data/melbourne-home-building" element={<MelbourneDataPage />} />
          {/* VIC Phase B (2026-03-06) - 8 climate + layout hubs.
              Info-only. Data-driven: shared wrapper reads slug from URL. */}
          <Route path="/melbourne-climate-and-home-design" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/passive-solar-design-melbourne" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/bushfire-attack-level-melbourne" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/thermal-mass-and-insulation-melbourne" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/orientation-and-site-planning-melbourne" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/home-layouts-melbourne" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/melbourne-family-home-floorplans" element={<MelbourneClimateOrLayoutPage />} />
          <Route path="/melbourne-narrow-block-designs" element={<MelbourneClimateOrLayoutPage />} />
          {/* VIC Phase C (2026-03-06) - 15 specialist "what to look for" pages.
              Info-only per locked VIC content rule - no builder shortlists. */}
          <Route path="/what-to-look-for-in-a-custom-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-volume-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-project-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-luxury-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-knockdown-rebuild-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-granny-flat-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-dual-occupancy-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-kitchen-renovator-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-bathroom-renovator-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-home-extension-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-deck-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-pergola-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-pool-builder-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-heritage-renovation-specialist-melbourne" element={<MelbourneSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-sustainable-home-builder-melbourne" element={<MelbourneSpecialistPage />} />
          {/* Adelaide specialist pages (SA equivalents of the 15 above, reciprocal geo-variant) */}
          <Route path="/what-to-look-for-in-a-custom-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-volume-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-project-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-luxury-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-knockdown-rebuild-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-granny-flat-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-dual-occupancy-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-kitchen-renovator-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-bathroom-renovator-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-home-extension-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-deck-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-pergola-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-pool-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-heritage-renovation-specialist-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/what-to-look-for-in-a-sustainable-home-builder-adelaide" element={<AdelaideSpecialistPage />} />
          <Route path="/land-for-sale-adelaide" element={<LandHubPage />} />
          <Route path="/land-for-sale-adelaide/:suburbSlug/:listingSlug" element={<LandListingDetailPage />} />
          <Route path="/suppliers/category/:slug" element={<SupplierCategoryPage />} />
          <Route path="/suppliers/guides/:slug" element={<SupplierGuidePage />} />
          <Route path="/learn" element={<LearnIndexPage />} />
          <Route path="/products" element={<ProductsPage />} />

          <Route path="/products/:category" element={<ProductsCategoryPage />} />
          <Route path="/best-outdoor-furniture" element={<MatchedRoundupPage slug="best-outdoor-furniture" />} />
          <Route path="/best-sofas-australia" element={<MatchedRoundupPage slug="best-sofas-australia" />} />
          <Route path="/best-rugs-australia" element={<MatchedRoundupPage slug="best-rugs-australia" />} />
          <Route path="/best-dining-tables-australia" element={<MatchedRoundupPage slug="best-dining-tables-australia" />} />
          <Route path="/styling-your-new-build" element={<InteriorStylingHubPage />} />
          <Route path="/renovation-finishing-touches" element={<RenoFinishingPage />} />
          <Route path="/learn/build-type/:slug" element={<LearnGuidePage />} />
          <Route path="/learn/trade/:slug" element={<LearnGuidePage />} />
          <Route path="/learn/bedroom-and-sleep" element={<LearnClusterHubPage />} />
          <Route path="/learn/outdoor-and-yard" element={<LearnClusterHubPage />} />
          <Route path="/learn/home-cleaning-and-move-in" element={<LearnClusterHubPage />} />
          <Route path="/learn/styling-your-new-home" element={<LearnClusterHubPage />} />
          <Route path="/learn/pergolas-and-outdoor-living" element={<LearnClusterHubPage />} />
          <Route path="/learn/furnishing-your-home" element={<LearnClusterHubPage />} />
          <Route path="/learn/bedroom-furnishing" element={<LearnClusterHubPage />} />
          <Route path="/learn/home-office" element={<LearnClusterHubPage />} />
          <Route path="/learn/kitchen-and-cookware" element={<LearnClusterHubPage />} />
          <Route path="/learn/tableware-and-glassware" element={<LearnClusterHubPage />} />
          <Route path="/learn/:slug" element={<LearnGuidePage />} />
          <Route path="/suppliers/:slug" element={<BusinessProfilePage />} />
          {/* Unified DB-driven profile routes - render BusinessProfilePage
              from the new /api/businesses/by-slug/:slug endpoint. Adding
              a new tradie / professional / business in Supabase
              auto-creates a working profile page at these URLs. */}
          <Route path="/tradies/:slug" element={<BusinessProfilePage />} />
          <Route path="/professionals/:slug" element={<BusinessProfilePage />} />
          <Route path="/business/:slug" element={<BusinessProfilePage />} />

          {/* Search results + admin insights */}
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/admin/search-insights" element={<SearchInsightsPage />} />
          <Route path="/admin/builder-drift" element={<BuilderDriftPage />} />
          <Route path="/admin/gsc-striking-distance" element={<StrikingDistancePage />} />
          <Route path="/admin/advertiser-performance" element={<AdvertiserPerformancePage />} />
          <Route path="/admin/onboarding" element={<AdminOnboardingPage />} />
          <Route path="/register-business" element={<BusinessOnboardingPage />} />

          {/* Sprint 3 (2026-06-03), /find anonymous-funnel pages.
              4 super-cat tiles + 58 sub-cat landing pages, all
              data-driven from /app/frontend/src/data/taxonomy.js. */}
          <Route path="/find" element={<FindHub />} />
          <Route path="/find/builder" element={<AudienceMatcherRoute audience="builder" />} />
          <Route path="/find/supplier" element={<AudienceMatcherRoute audience="supplier" />} />
          <Route path="/find/trade" element={<AudienceMatcherRoute audience="trade" />} />
          <Route path="/find/professional" element={<AudienceMatcherRoute audience="professional" />} />
          <Route path="/find/category/:supSlug" element={<FindSuperCategory />} />
          <Route path="/find/:subSlug" element={<FindCategory />} />

          <Route path="/our-community" element={<Navigate to="/community" replace />} />
          <Route path="/facebook-groups" element={<Navigate to="/community" replace />} />
          <Route path="/premium-support" element={<Navigate to="/one-on-one-support" replace />} />
          <Route path="/premium-assistance" element={<Navigate to="/one-on-one-support" replace />} />
          
          {/* SEO Landing Pages - High-value search terms */}
          <Route path="/best-home-builders-adelaide" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/home-builders-adelaide" element={<><SaVicHreflangPair selfPath="/home-builders-adelaide" selfState="SA" siblingPath="/home-builders-melbourne" /><HomeBuildersAdelaide /></>} />
          <Route path="/adelaide-home-builders" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/adelaide-builders" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/questions-to-ask-builders" element={<QuestionsToAskBuildersPage />} />
          <Route path="/hiring-trades-guide" element={<HiringTradesGuide />} />
          <Route path="/hiring-professionals-guide" element={<HiringProfessionalsGuide />} />
          <Route path="/verify-a-business" element={<VerifyABusinessPage />} />
          <Route path="/builder-checklist" element={<Navigate to="/questions-to-ask-builders" replace />} />
          <Route path="/building-costs-adelaide-2026" element={<BuildingCostsAdelaidePage />} />
          <Route path="/cost-to-build-australia" element={<Navigate to="/cost-to-build-a-house-australia" replace />} />
          <Route path="/adelaide-build-cost-report-2026" element={<><SaVicHreflangPair selfPath="/adelaide-build-cost-report-2026" selfState="SA" siblingPath="/melbourne-build-cost-report-2026" /><AdelaideBuildCostReportPage /></>} />
          <Route path="/adelaide-build-cost-report" element={<Navigate to="/adelaide-build-cost-report-2026" replace />} />
          <Route path="/building-costs-adelaide" element={<Navigate to="/building-costs-adelaide-2026" replace />} />
          <Route path="/adelaide-building-costs" element={<Navigate to="/building-costs-adelaide-2026" replace />} />
          <Route path="/building-partners" element={<BuildingPartnersPage />} />
          <Route path="/our-builders" element={<Navigate to="/building-partners" replace />} />
          <Route path="/partner-builders" element={<Navigate to="/building-partners" replace />} />

          {/* AI SEO / GEO authority + programmatic pages */}
          <Route path="/adelaide-builders-index" element={<AdelaideBuildersIndexPage />} />
          <Route path="/ai-agents" element={<AIAgentsPage />} />
          <Route path="/builders-index" element={<Navigate to="/adelaide-builders-index" replace />} />
          <Route path="/about/editorial-team" element={<EditorialTeamPage />} />
          <Route path="/editorial-team" element={<Navigate to="/about/editorial-team" replace />} />
          <Route path="/home-builders-in/:suburbSlug" element={<BuilderInSuburbPage />} />
          <Route path="/floor-plans/:variantSlug" element={<HomeDesignsLandingPage />} />

          {/* Phase 1 legal / transparency pages */}
          <Route path="/legal/one-to-one-support-agreement" element={<OneToOneSupportAgreementPage />} />
          <Route path="/legal/advisory-agreement" element={<Navigate to="/legal/one-to-one-support-agreement" replace />} />
          <Route path="/legal/recommendations" element={<RecommendationsPolicyPage />} />
          <Route path="/legal/advertising" element={<AdvertisingPolicyPage />} />
          <Route path="/legal/commercial-disclosure" element={<CommercialDisclosurePage />} />
          <Route path="/legal/property-listings" element={<PropertyListingsPolicyPage />} />
          <Route path="/about/how-we-make-money" element={<HowWeMakeMoneyPage />} />
          <Route path="/business/advertise" element={<BusinessAdvertisePage />} />
          
          {/* Near Me & First Home Buyer Pages */}
          <Route path="/home-builders-near-me-adelaide" element={<HomeBuildersNearMePage />} />
          <Route path="/home-builders-near-me" element={<Navigate to="/home-builders-near-me-adelaide" replace />} />
          <Route path="/builders-near-me" element={<Navigate to="/home-builders-near-me-adelaide" replace />} />
          <Route path="/local-builders-adelaide" element={<Navigate to="/home-builders-near-me-adelaide" replace />} />
          <Route path="/first-home-buyer-adelaide" element={<><SaVicHreflangPair selfPath="/first-home-buyer-adelaide" selfState="SA" siblingPath="/first-home-buyer-melbourne" /><FirstHomeBuyerPage /></>} />
          <Route path="/first-home-buyer" element={<Navigate to="/first-home-buyer-adelaide" replace />} />
          <Route path="/first-home-owner-grant-adelaide" element={<Navigate to="/first-home-buyer-adelaide" replace />} />
          <Route path="/fhog-adelaide" element={<Navigate to="/first-home-buyer-adelaide" replace />} />
          
          {/* Builder Comparison Pages */}
          <Route path="/compare" element={<CompareIndexPage />} />
          <Route path="/compare/:slug" element={<BuilderComparisonPage />} />
          
          {/* Display Home Guide */}
          <Route path="/display-home-guide" element={<DisplayHomeGuidePage />} />
          <Route path="/display-homes-guide" element={<Navigate to="/display-home-guide" replace />} />
          <Route path="/visiting-display-homes" element={<Navigate to="/display-home-guide" replace />} />
          
          {/* Display Homes Adelaide - SEO Mega Guide */}
          <Route path="/display-homes-adelaide" element={<DisplayHomesAdelaide />} />
          {/* National + per-state display homes directory (Supabase-sourced, scraped) */}
          <Route path="/display-homes" element={<DisplayHomesHubPage />} />
          <Route path="/display-homes/:state" element={<DisplayHomesHubPage />} />
          <Route path="/display-homes-near-me-adelaide" element={<Navigate to="/display-homes-adelaide" replace />} />
          <Route path="/display-villages-adelaide" element={<Navigate to="/display-homes-adelaide" replace />} />
          
          {/* Display Home Checklist - Lead Magnet */}
          <Route path="/display-home-checklist" element={<DisplayHomeChecklist />} />
          
          {/* Builder Type SEO Pages */}
          {/* 2026-07-05 cannibalisation collapse: /top-10-... now 301s to /home-builders-adelaide (real 301 in serve.json + _redirects). The <Navigate> below covers client-side SPA navigation. */}
          <Route path="/top-10-home-builders-adelaide" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/top-5-home-builders-adelaide" element={<BuilderTypePage />} />
          <Route path="/budget-home-builders-adelaide" element={<BuilderTypePage />} />
          <Route path="/luxury-home-builders-adelaide" element={<BuilderTypePage />} />
          <Route path="/dodgy-builders-adelaide" element={<BuilderTypePage />} />
          <Route path="/adelaide-builders-list" element={<BuilderTypePage />} />
          <Route path="/home-builders-adelaide-hills" element={<BuilderTypePage />} />
          
          {/* Display Home Location SEO Pages */}
          <Route path="/display-homes-adelaide-hills" element={<DisplayHomeLocationPage />} />
          <Route path="/display-homes-munno-para" element={<DisplayHomeLocationPage />} />
          
          {/* Builder Type Comparison Pages */}
          <Route path="/volume-builder-vs-project-builder-adelaide" element={<BuilderTypeComparisonPage />} />
          <Route path="/volume-builder-vs-custom-builder-adelaide" element={<BuilderTypeComparisonPage />} />
          <Route path="/project-builder-vs-custom-builder-adelaide" element={<BuilderTypeComparisonPage />} />
          
          {/* Best Builder For Pages */}
          <Route path="/best-builder-for-first-home-buyers" element={<BestBuilderForPage />} />
          <Route path="/best-builder-for-two-storey-homes" element={<BestBuilderForPage />} />
          <Route path="/best-builder-for-knockdown-rebuild" element={<BestBuilderForPage />} />
          <Route path="/best-builder-for-acreage" element={<BestBuilderForPage />} />
          
          {/* Referral Page */}
          <Route path="/referred" element={<ReferralPage />} />
          <Route path="/referral" element={<Navigate to="/referred" replace />} />
          <Route path="/you-were-referred" element={<Navigate to="/referred" replace />} />
          <Route path="/share" element={<ShareReferralPage />} />
          <Route path="/share-referral" element={<Navigate to="/share" replace />} />
          <Route path="/refer-a-friend" element={<Navigate to="/share" replace />} />
          
          {/* Regional Landing Pages.
              /home-builders-adelaide-hills is intentionally NOT mounted
              here — it's owned by BuilderTypePage on line 728 above.
              The regionalData.js entry for `adelaide-hills` is retained
              for future reuse but is currently unreachable. */}
          <Route path="/home-builders-northern-adelaide" element={<RegionalLandingPage />} />
          <Route path="/home-builders-southern-adelaide" element={<RegionalLandingPage />} />
          <Route path="/home-builders-western-adelaide" element={<RegionalLandingPage />} />
          
          {/* Decision Comparison Pages */}
          <Route path="/building-vs-buying" element={<DecisionComparisonPage />} />
          <Route path="/single-vs-double-storey" element={<DecisionComparisonPage />} />
          <Route path="/new-build-vs-knockdown-rebuild" element={<DecisionComparisonPage />} />
          {/* Outdoor Living comparison long-tail (2026-02-28) — AEO wins */}
          <Route path="/pergola-vs-verandah-adelaide" element={<DecisionComparisonPage />} />
          <Route path="/timber-vs-composite-decking-adelaide" element={<DecisionComparisonPage />} />
          <Route path="/concrete-vs-fibreglass-pool-adelaide" element={<DecisionComparisonPage />} />
          <Route path="/whole-home-renovation-vs-single-room-adelaide" element={<DecisionComparisonPage />} />
          {/* Additional SA comparisons (2026-03-05) - completes the SA outdoor +
              renovation + granny flat comparison cluster. */}
          <Route path="/open-plan-vs-galley-kitchen-adelaide" element={<DecisionComparisonPage />} />
          <Route path="/granny-flat-vs-extension-adelaide" element={<GrannyFlatVsExtensionAdelaidePage />} />
          {/* Melbourne comparison pages (VIC Phase 1, 2026-03-05) - VIC mirror of
              the SA outdoor / renovation comparison cluster. Same template,
              independent data entries in decisionComparisonData.js. */}
          <Route path="/pergola-vs-verandah-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/timber-vs-composite-decking-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/concrete-vs-fibreglass-pool-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/knockdown-vs-renovate-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/single-vs-double-storey-melbourne" element={<DecisionComparisonPage />} />
          {/* Phase A additions (2026-03-05) - 3 remaining VIC comparisons to parity with SA */}
          <Route path="/whole-home-renovation-vs-single-room-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/kdr-vs-buy-existing-melbourne" element={<DecisionComparisonPage />} />
          <Route path="/aging-parents-moving-melbourne" element={<DecisionComparisonPage />} />

          {/* Melbourne suburb variants (VIC Phase 1 Batch D, 2026-03-05) - long-tail
              local pages linking UP to Melbourne pillars. Independent of the
              SA outdoor / renovation suburb data. */}
          <Route path="/pool-builders-in-brighton" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-glen-waverley" element={<MelbourneSuburbPage />} />
          <Route path="/verandah-builders-in-frankston" element={<MelbourneSuburbPage />} />
          <Route path="/verandah-builders-in-mornington" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-carlton" element={<MelbourneSuburbPage />} />
          <Route path="/bathroom-renovations-in-hawthorn" element={<MelbourneSuburbPage />} />

          {/* VIC Phase D Batch 1 (2026-03-06) - 20 trade × suburb long-tail pages.
              4 trades (architects, kitchen, bathroom, extensions) × 5 suburbs
              (Hawthorn, Brighton, Toorak, Kew, Malvern). All info-only per VIC
              content rule. Powered by same MelbourneSuburbPage wrapper +
              extended melbourneSuburbPagesData.js buildTradeSuburb() helper. */}
          <Route path="/architects-in-hawthorn" element={<MelbourneSuburbPage />} />
          <Route path="/architects-in-brighton" element={<MelbourneSuburbPage />} />
          <Route path="/architects-in-toorak" element={<MelbourneSuburbPage />} />
          <Route path="/architects-in-kew" element={<MelbourneSuburbPage />} />
          <Route path="/architects-in-malvern" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-hawthorn" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-brighton" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-toorak" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-kew" element={<MelbourneSuburbPage />} />
          <Route path="/kitchen-renovations-in-malvern" element={<MelbourneSuburbPage />} />
          <Route path="/bathroom-renovations-in-brighton" element={<MelbourneSuburbPage />} />
          <Route path="/bathroom-renovations-in-toorak" element={<MelbourneSuburbPage />} />
          <Route path="/bathroom-renovations-in-kew" element={<MelbourneSuburbPage />} />
          <Route path="/bathroom-renovations-in-malvern" element={<MelbourneSuburbPage />} />
          <Route path="/home-extensions-in-hawthorn" element={<MelbourneSuburbPage />} />
          <Route path="/home-extensions-in-brighton" element={<MelbourneSuburbPage />} />
          <Route path="/home-extensions-in-toorak" element={<MelbourneSuburbPage />} />
          <Route path="/home-extensions-in-kew" element={<MelbourneSuburbPage />} />
          <Route path="/home-extensions-in-malvern" element={<MelbourneSuburbPage />} />

          {/* VIC Phase D Batch 2 (2026-03-07) - 25 more trade × suburb long-tail pages.
              5 outdoor + rebuild trades (deck / pergola / pool / custom / granny-flat)
              × 5 heritage-dense middle-ring suburbs (Bentleigh / Camberwell /
              Elsternwick / Armadale / Prahran). Info-only. Same wrapper + helper. */}
          <Route path="/deck-builders-in-bentleigh" element={<MelbourneSuburbPage />} />
          <Route path="/deck-builders-in-camberwell" element={<MelbourneSuburbPage />} />
          <Route path="/deck-builders-in-elsternwick" element={<MelbourneSuburbPage />} />
          <Route path="/deck-builders-in-armadale" element={<MelbourneSuburbPage />} />
          <Route path="/deck-builders-in-prahran" element={<MelbourneSuburbPage />} />
          <Route path="/pergola-builders-in-bentleigh" element={<MelbourneSuburbPage />} />
          <Route path="/pergola-builders-in-camberwell" element={<MelbourneSuburbPage />} />
          <Route path="/pergola-builders-in-elsternwick" element={<MelbourneSuburbPage />} />
          <Route path="/pergola-builders-in-armadale" element={<MelbourneSuburbPage />} />
          <Route path="/pergola-builders-in-prahran" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-bentleigh" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-camberwell" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-elsternwick" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-armadale" element={<MelbourneSuburbPage />} />
          <Route path="/pool-builders-in-prahran" element={<MelbourneSuburbPage />} />
          <Route path="/custom-home-builders-in-bentleigh" element={<MelbourneSuburbPage />} />
          <Route path="/custom-home-builders-in-camberwell" element={<MelbourneSuburbPage />} />
          <Route path="/custom-home-builders-in-elsternwick" element={<MelbourneSuburbPage />} />
          <Route path="/custom-home-builders-in-armadale" element={<MelbourneSuburbPage />} />
          <Route path="/custom-home-builders-in-prahran" element={<MelbourneSuburbPage />} />
          <Route path="/granny-flat-builders-in-bentleigh" element={<MelbourneSuburbPage />} />
          <Route path="/granny-flat-builders-in-camberwell" element={<MelbourneSuburbPage />} />
          <Route path="/granny-flat-builders-in-elsternwick" element={<MelbourneSuburbPage />} />
          <Route path="/granny-flat-builders-in-armadale" element={<MelbourneSuburbPage />} />
          <Route path="/granny-flat-builders-in-prahran" element={<MelbourneSuburbPage />} />

          {/* VIC Phase D Batch 3 (2026-03-07) - 25 single-trade × outer-middle-ring suburb pages */}
          <Route path="/electricians-in-bentleigh-east" element={<MelbourneSuburbPage />} />
          <Route path="/electricians-in-ashwood" element={<MelbourneSuburbPage />} />
          <Route path="/electricians-in-reservoir" element={<MelbourneSuburbPage />} />
          <Route path="/electricians-in-preston" element={<MelbourneSuburbPage />} />
          <Route path="/electricians-in-coburg" element={<MelbourneSuburbPage />} />
          <Route path="/plumbers-in-bentleigh-east" element={<MelbourneSuburbPage />} />
          <Route path="/plumbers-in-ashwood" element={<MelbourneSuburbPage />} />
          <Route path="/plumbers-in-reservoir" element={<MelbourneSuburbPage />} />
          <Route path="/plumbers-in-preston" element={<MelbourneSuburbPage />} />
          <Route path="/plumbers-in-coburg" element={<MelbourneSuburbPage />} />
          <Route path="/painters-in-bentleigh-east" element={<MelbourneSuburbPage />} />
          <Route path="/painters-in-ashwood" element={<MelbourneSuburbPage />} />
          <Route path="/painters-in-reservoir" element={<MelbourneSuburbPage />} />
          <Route path="/painters-in-preston" element={<MelbourneSuburbPage />} />
          <Route path="/painters-in-coburg" element={<MelbourneSuburbPage />} />
          <Route path="/tilers-in-bentleigh-east" element={<MelbourneSuburbPage />} />
          <Route path="/tilers-in-ashwood" element={<MelbourneSuburbPage />} />
          <Route path="/tilers-in-reservoir" element={<MelbourneSuburbPage />} />
          <Route path="/tilers-in-preston" element={<MelbourneSuburbPage />} />
          <Route path="/tilers-in-coburg" element={<MelbourneSuburbPage />} />
          <Route path="/plasterers-in-bentleigh-east" element={<MelbourneSuburbPage />} />
          <Route path="/plasterers-in-ashwood" element={<MelbourneSuburbPage />} />
          <Route path="/plasterers-in-reservoir" element={<MelbourneSuburbPage />} />
          <Route path="/plasterers-in-preston" element={<MelbourneSuburbPage />} />
          <Route path="/plasterers-in-coburg" element={<MelbourneSuburbPage />} />

          {/* VIC Phase D Batch 4 (2026-03-07) - 25 outdoor + structural × west/inner-north suburbs */}
          <Route path="/landscapers-in-yarraville" element={<MelbourneSuburbPage />} />
          <Route path="/landscapers-in-newport" element={<MelbourneSuburbPage />} />
          <Route path="/landscapers-in-williamstown" element={<MelbourneSuburbPage />} />
          <Route path="/landscapers-in-northcote" element={<MelbourneSuburbPage />} />
          <Route path="/landscapers-in-thornbury" element={<MelbourneSuburbPage />} />
          <Route path="/concreters-in-yarraville" element={<MelbourneSuburbPage />} />
          <Route path="/concreters-in-newport" element={<MelbourneSuburbPage />} />
          <Route path="/concreters-in-williamstown" element={<MelbourneSuburbPage />} />
          <Route path="/concreters-in-northcote" element={<MelbourneSuburbPage />} />
          <Route path="/concreters-in-thornbury" element={<MelbourneSuburbPage />} />
          <Route path="/carpenters-in-yarraville" element={<MelbourneSuburbPage />} />
          <Route path="/carpenters-in-newport" element={<MelbourneSuburbPage />} />
          <Route path="/carpenters-in-williamstown" element={<MelbourneSuburbPage />} />
          <Route path="/carpenters-in-northcote" element={<MelbourneSuburbPage />} />
          <Route path="/carpenters-in-thornbury" element={<MelbourneSuburbPage />} />
          <Route path="/roofers-in-yarraville" element={<MelbourneSuburbPage />} />
          <Route path="/roofers-in-newport" element={<MelbourneSuburbPage />} />
          <Route path="/roofers-in-williamstown" element={<MelbourneSuburbPage />} />
          <Route path="/roofers-in-northcote" element={<MelbourneSuburbPage />} />
          <Route path="/roofers-in-thornbury" element={<MelbourneSuburbPage />} />
          <Route path="/renderers-in-yarraville" element={<MelbourneSuburbPage />} />
          <Route path="/renderers-in-newport" element={<MelbourneSuburbPage />} />
          <Route path="/renderers-in-williamstown" element={<MelbourneSuburbPage />} />
          <Route path="/renderers-in-northcote" element={<MelbourneSuburbPage />} />
          <Route path="/renderers-in-thornbury" element={<MelbourneSuburbPage />} />

          {/* Melbourne pillar clusters (VIC Phase 1 Batch B, 2026-03-05) - VIC mirror
              of SA outdoor / renovation / granny-flat pillars. Independent
              data source (melbournePillarData.js); SA pillars untouched. */}
          <Route path="/swimming-pool-builders-melbourne" element={<MelbournePillarPage />} />
          <Route path="/verandahs-and-pergolas-melbourne" element={<MelbournePillarPage />} />
          <Route path="/decking-melbourne" element={<MelbournePillarPage />} />
          <Route path="/sheds-and-garages-melbourne" element={<MelbournePillarPage />} />
          <Route path="/outdoor-living-melbourne" element={<MelbournePillarPage />} />
          <Route path="/kitchen-renovations-melbourne" element={<MelbournePillarPage />} />
          <Route path="/bathroom-renovations-melbourne" element={<MelbournePillarPage />} />
          <Route path="/granny-flat-builders-melbourne" element={<MelbournePillarPage />} />
          
          {/* Ultimate Building Guide */}
          <Route path="/complete-guide-building-adelaide" element={<UltimateGuidePage />} />
          <Route path="/complete-guide-building-melbourne" element={<MelbourneUltimateGuidePage />} />
          <Route path="/building-guide-adelaide" element={<Navigate to="/complete-guide-building-adelaide" replace />} />
          <Route path="/how-to-build-a-house-adelaide" element={<Navigate to="/complete-guide-building-adelaide" replace />} />
          
          {/* FAQ / People Also Ask Page */}
          <Route path="/building-questions-adelaide" element={<FaqLandingPage />} />
          <Route path="/building-faq" element={<Navigate to="/building-questions-adelaide" replace />} />
          
          {/* Market Update Pages */}
          <Route path="/market-update" element={<MarketUpdatePage />} />
          <Route path="/market-update/:slug" element={<MarketUpdatePage />} />
          <Route path="/adelaide-building-market" element={<Navigate to="/market-update" replace />} />

          {/* Editorial process / E-E-A-T page */}
          <Route path="/about/editorial-process" element={<EditorialProcessPage />} />
          <Route path="/editorial-process" element={<Navigate to="/about/editorial-process" replace />} />
          <Route path="/how-it-works" element={<HowBuildPilotWorksPage />} />
          <Route path="/how-buildpilot-works" element={<Navigate to="/how-it-works" replace />} />

          {/* Programmatic SEO pages: budget×region + type×region (32 pages) */}
          {getAllProgrammaticSlugs().map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<ProgrammaticSeoPage />} />
          ))}

          {/* Lead magnet pages */}
          <Route path="/lead-magnets/build-cost-benchmark" element={<LeadMagnetPage />} />
          <Route path="/lead-magnets/builder-shortlist-scorecard" element={<LeadMagnetPage />} />
          <Route path="/lead-magnets/settlement-timeline" element={<LeadMagnetPage />} />

          {/* Case studies - index hub + dynamic per-client pages */}
          <Route path="/case-studies" element={<CaseStudiesIndexPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
          
          {/* Project Type Pages */}
          <Route path="/granny-flat-builders-adelaide" element={<ProjectTypePage />} />
          <Route path="/dual-occupancy-adelaide" element={<ProjectTypePage />} />
          <Route path="/townhouse-builders-adelaide" element={<ProjectTypePage />} />
          <Route path="/custom-home-builders-adelaide" element={<ProjectTypePage />} />
          
          {/* Builder Category Pages (targeting search terms) */}
          <Route path="/small-home-builders-adelaide" element={<SmallHomeBuildersPage />} />
          <Route path="/transportable-homes-adelaide" element={<TransportableHomesPage />} />
          <Route path="/modular-homes-adelaide" element={<Navigate to="/transportable-homes-adelaide" replace />} />
          <Route path="/prefab-homes-adelaide" element={<Navigate to="/transportable-homes-adelaide" replace />} />
          <Route path="/country-home-builders-south-australia" element={<CountryBuildersPage />} />
          <Route path="/rural-builders-sa" element={<Navigate to="/country-home-builders-south-australia" replace />} />
          <Route path="/regional-builders-adelaide" element={<Navigate to="/country-home-builders-south-australia" replace />} />
          <Route path="/house-building-costs-by-bedroom-adelaide" element={<><SaVicHreflangPair selfPath="/house-building-costs-by-bedroom-adelaide" selfState="SA" siblingPath="/house-building-costs-by-bedroom-melbourne" /><BedroomPricingPage /></>} />
          <Route path="/house-building-costs-by-bedroom-melbourne" element={<><SaVicHreflangPair selfPath="/house-building-costs-by-bedroom-melbourne" selfState="VIC" siblingPath="/house-building-costs-by-bedroom-adelaide" /><BedroomPricingMelbournePage /></>} />
          <Route path="/3-bedroom-house-cost-melbourne" element={<Navigate to="/house-building-costs-by-bedroom-melbourne" replace />} />
          <Route path="/4-bedroom-house-cost-melbourne" element={<Navigate to="/house-building-costs-by-bedroom-melbourne" replace />} />
          <Route path="/house-builders-price-list-melbourne" element={<Navigate to="/house-building-costs-by-bedroom-melbourne" replace />} />
          <Route path="/house-builders-price-list-adelaide" element={<Navigate to="/house-building-costs-by-bedroom-adelaide" replace />} />
          <Route path="/3-bedroom-house-cost-adelaide" element={<Navigate to="/house-building-costs-by-bedroom-adelaide" replace />} />
          <Route path="/4-bedroom-house-cost-adelaide" element={<Navigate to="/house-building-costs-by-bedroom-adelaide" replace />} />
          
          {/* New SEO Pages - "People Also Ask" targeting */}
          <Route path="/reliable-builders-adelaide" element={<ReliableBuildersPage />} />
          <Route path="/most-reliable-builders-adelaide" element={<Navigate to="/reliable-builders-adelaide" replace />} />
          <Route path="/trustworthy-builders-adelaide" element={<Navigate to="/reliable-builders-adelaide" replace />} />
          <Route path="/build-vs-buy-adelaide" element={<BuildVsBuyPage />} />
          <Route path="/build-or-buy-house-adelaide" element={<Navigate to="/build-vs-buy-adelaide" replace />} />
          <Route path="/cheaper-to-build-or-buy-adelaide" element={<Navigate to="/build-vs-buy-adelaide" replace />} />
          <Route path="/growth-suburbs-adelaide" element={<GrowthSuburbsPage />} />
          <Route path="/best-suburbs-to-build-adelaide" element={<Navigate to="/growth-suburbs-adelaide" replace />} />
          <Route path="/which-suburbs-will-boom-adelaide" element={<Navigate to="/growth-suburbs-adelaide" replace />} />
          
          {/* New Research & Comparison Pages */}
          <Route path="/which-builder-should-i-choose" element={<WhichBuilderPage />} />
          <Route path="/build-decisions" element={<BuildDecisionsHubPage />} />
          <Route path="/research" element={<ResearchCompareHubPage />} />
          <Route path="/research/:categorySlug" element={<ResearchDirectoryPage />} />
          <Route path="/cost-index" element={<CostIndexPage />} />
          <Route path="/reports" element={<ReportsHubPage />} />
          <Route path="/reports/australian-home-building-cost-report-2026" element={<BuildingCostReport2026Page />} />
          <Route path="/owning-your-home" element={<Navigate to="/lifecycle/love-living" replace />} />
          <Route path="/building-decisions-adelaide" element={<Navigate to="/build-decisions" replace />} />
          <Route path="/how-to-choose-a-builder-adelaide" element={<Navigate to="/which-builder-should-i-choose" replace />} />
          <Route path="/choosing-a-builder-adelaide" element={<Navigate to="/which-builder-should-i-choose" replace />} />
          <Route path="/build-times-adelaide" element={<><SaVicHreflangPair selfPath="/build-times-adelaide" selfState="SA" siblingPath="/build-times-melbourne" /><BuildTimesPage /></>} />
          <Route path="/build-times-melbourne" element={<><SaVicHreflangPair selfPath="/build-times-melbourne" selfState="VIC" siblingPath="/build-times-adelaide" /><BuildTimesMelbournePage /></>} />
          <Route path="/average-build-time-melbourne" element={<Navigate to="/build-times-melbourne" replace />} />
          <Route path="/construction-timeline-melbourne" element={<Navigate to="/build-times-melbourne" replace />} />

          {/* Melbourne / VIC builder-category landing pages (self-canonical, VIC-defaulted matcher) */}
          <Route path="/custom-home-builders-melbourne" element={<><SaVicHreflangPair selfPath="/custom-home-builders-melbourne" selfState="VIC" siblingPath="/custom-home-builders-adelaide" /><MelbourneBuilderTypePage /></>} />
          <Route path="/luxury-home-builders-melbourne" element={<><SaVicHreflangPair selfPath="/luxury-home-builders-melbourne" selfState="VIC" siblingPath="/luxury-home-builders-adelaide" /><MelbourneBuilderTypePage /></>} />
          <Route path="/budget-home-builders-melbourne" element={<><SaVicHreflangPair selfPath="/budget-home-builders-melbourne" selfState="VIC" siblingPath="/budget-home-builders-adelaide" /><MelbourneBuilderTypePage /></>} />
          <Route path="/townhouse-builders-melbourne" element={<><SaVicHreflangPair selfPath="/townhouse-builders-melbourne" selfState="VIC" siblingPath="/townhouse-builders-adelaide" /><MelbourneBuilderTypePage /></>} />
          <Route path="/small-home-builders-melbourne" element={<><SaVicHreflangPair selfPath="/small-home-builders-melbourne" selfState="VIC" siblingPath="/small-home-builders-adelaide" /><MelbourneBuilderTypePage /></>} />
          <Route path="/volume-home-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/new-home-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/first-home-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/double-storey-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/knockdown-rebuild-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/split-level-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/narrow-block-builders-melbourne" element={<MelbourneBuilderTypePage />} />
          <Route path="/how-long-to-build-house-adelaide" element={<Navigate to="/build-times-adelaide" replace />} />
          <Route path="/average-build-time-adelaide" element={<Navigate to="/build-times-adelaide" replace />} />
          <Route path="/construction-timeline-adelaide" element={<Navigate to="/build-times-adelaide" replace />} />
          <Route path="/common-builder-problems" element={<CommonBuilderProblemsPage />} />
          <Route path="/builder-complaints-adelaide" element={<Navigate to="/common-builder-problems" replace />} />
          <Route path="/builder-issues-adelaide" element={<Navigate to="/common-builder-problems" replace />} />
          
          {/* Best Builder for [Suburb] Pages */}
          <Route path="/best-builder-for/:suburb" element={<BestBuilderForSuburbPage />} />
          <Route path="/best-builder-mount-barker" element={<Navigate to="/best-builder-for/mount-barker" replace />} />
          <Route path="/best-builder-gawler" element={<Navigate to="/best-builder-for/gawler" replace />} />
          <Route path="/best-builder-seaford" element={<Navigate to="/best-builder-for/seaford" replace />} />
          <Route path="/best-builder-two-wells" element={<Navigate to="/best-builder-for/two-wells" replace />} />
          <Route path="/best-builder-aldinga" element={<Navigate to="/best-builder-for/aldinga" replace />} />
          <Route path="/best-builder-angle-vale" element={<Navigate to="/best-builder-for/angle-vale" replace />} />
          <Route path="/best-builder-elizabeth" element={<Navigate to="/best-builder-for/elizabeth" replace />} />
          <Route path="/best-builder-mawson-lakes" element={<Navigate to="/best-builder-for/mawson-lakes" replace />} />
          <Route path="/best-builder-morphett-vale" element={<Navigate to="/best-builder-for/morphett-vale" replace />} />
          <Route path="/best-builder-munno-para" element={<Navigate to="/best-builder-for/munno-para" replace />} />
          <Route path="/best-builder-golden-grove" element={<Navigate to="/best-builder-for/golden-grove" replace />} />
          
          {/* Council Guide Pages */}
          <Route path="/council-approvals-adelaide-guide" element={<CouncilApprovalsAdelaideGuidePage />} />
          <Route path="/private-certification-sa-guide" element={<PrivateCertificationSAGuidePage />} />
          <Route path="/block-check-adelaide" element={<BlockCheckHubPage />} />
          <Route path="/block-check-adelaide/:slug" element={<BlockCheckPillarPage />} />
          <Route path="/verify-your-builder" element={<VerifyBuilderHubPage />} />
          <Route path="/verify-your-builder/:slug" element={<VerifyBuilderPillarPage />} />
          <Route path="/adelaide-climate-design" element={<ClimateDesignHubPage />} />
          <Route path="/adelaide-climate-design/:slug" element={<ClimateDesignPillarPage />} />
          <Route path="/adelaide-home-layout" element={<HomeLayoutHubPage />} />
          <Route path="/adelaide-home-layout/:slug" element={<HomeLayoutPillarPage />} />
          <Route path="/building-contracts-adelaide-guide" element={<BuildingContractsAdelaideGuidePage />} />
          <Route path="/property-investors-adelaide" element={<PropertyInvestorsAdelaidePage />} />
          <Route path="/fixed-price-vs-cost-plus-adelaide" element={<FixedPriceVsCostPlusAdelaidePage />} />
          <Route path="/infill-vs-greenfield-land-adelaide" element={<InfillVsGreenfieldLandAdelaidePage />} />
          <Route path="/difficult-block-builder-adelaide" element={<DifficultBlockBuilderAdelaidePage />} />
          <Route path="/i-own-land-already-adelaide" element={<IOwnLandAlreadyAdelaidePage />} />
          <Route path="/first-home-guarantee-scheme-adelaide" element={<FirstHomeGuaranteeSchemeAdelaidePage />} />
          <Route path="/first-home-guarantee-adelaide" element={<Navigate to="/first-home-guarantee-scheme-adelaide" replace />} />
          <Route path="/first-home-owner-grant-sa-adelaide" element={<FirstHomeOwnerGrantSAAdelaidePage />} />
          <Route path="/first-home-super-saver-adelaide" element={<FirstHomeSuperSaverAdelaidePage />} />
          <Route path="/homestart-finance-adelaide" element={<HomeStartFinanceAdelaidePage />} />
          <Route path="/stamp-duty-first-home-buyer-sa-adelaide" element={<StampDutyFirstHomeBuyerSAAdelaidePage />} />
          <Route path="/family-home-guarantee-adelaide" element={<FamilyHomeGuaranteeAdelaidePage />} />
          <Route path="/home-designs/collection/:slug" element={<HomeDesignsTaxonomyPage />} />
          <Route path="/metricon-vs-fairmont-homes-adelaide" element={<BuilderVsPage />} />
          <Route path="/metricon-vs-simonds-adelaide" element={<BuilderVsPage />} />
          <Route path="/metricon-vs-rossdale-adelaide" element={<BuilderVsPage />} />
          <Route path="/fairmont-vs-simonds-adelaide" element={<BuilderVsPage />} />
          <Route path="/rossdale-vs-format-homes-adelaide" element={<BuilderVsPage />} />
          <Route path="/homecorp-sa-vs-fairmont-homes-adelaide" element={<BuilderVsPage />} />
          <Route path="/alete-vs-format-homes-adelaide" element={<BuilderVsPage />} />
          <Route path="/oakford-vs-format-homes-adelaide" element={<BuilderVsPage />} />
          <Route path="/oakford-vs-rossdale-adelaide" element={<BuilderVsPage />} />
          <Route path="/budget-vs-premium-builders-adelaide" element={<BuilderVsPage />} />
          <Route path="/volume-vs-custom-builder-adelaide" element={<BuilderVsPage />} />
          <Route path="/matcher" element={<MatcherRoute />} />
          <Route path="/start" element={<StartBuildingPage />} />
          <Route path="/new-home-advisors" element={<NewHomeAdvisorsPage />} />
          <Route path="/building-timeline-adelaide-guide" element={<BuildingTimelineAdelaideGuidePage />} />
          <Route path="/warranty-and-after-handover-adelaide" element={<WarrantyAndAfterHandoverAdelaidePage />} />
          <Route path="/adelaide-building-market-report" element={<AdelaideBuildingMarketReport />} />
          <Route path="/build-vs-buy-adelaide-guide" element={<BuildVsBuyAdelaideGuidePage />} />
          {/* Custom HIA partner profile page (Feb-2026) has been retired
              in favour of the standard BusinessProfilePage template so
              every onboarded business shares one consistent profile
              shape. Redirect preserves inbound links (sitemap, partner
              blocks, cross-links). */}
          <Route path="/partner/house-inspect-australia" element={<Navigate to="/business/house-inspect-australia" replace />} />
          <Route path="/building-in-onkaparinga-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-playford-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-salisbury-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-tea-tree-gully-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-charles-sturt-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-mount-barker-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-marion-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-prospect-council" element={<CouncilGuidePage />} />
          <Route path="/building-in-burnside-council" element={<CouncilGuidePage />} />
          
          {/* Problem Pages */}
          <Route path="/builder-went-bankrupt-what-now" element={<ProblemPage />} />
          <Route path="/how-to-change-builders-mid-build" element={<ProblemPage />} />
          <Route path="/dispute-with-builder-adelaide" element={<ProblemPage />} />
          
          {/* Seasonal Content Pages */}
          <Route path="/best-time-to-start-building-adelaide" element={<SeasonalContentPage />} />
          <Route path="/building-in-summer-vs-winter" element={<SeasonalContentPage />} />
          
          {/* Interactive Tools */}
          <Route path="/builder-quiz" element={<BuilderQuizPage />} />
          <Route path="/ready-to-build-checklist" element={<ReadyToBuildPage />} />
          <Route path="/which-builder-quiz" element={<Navigate to="/builder-quiz" replace />} />
          <Route path="/am-i-ready-to-build" element={<Navigate to="/ready-to-build-checklist" replace />} />
          
          {/* House & Land Content Pages */}
          <Route path="/house-and-land-packages-adelaide" element={<HouseAndLandPage />} />
          <Route path="/home-and-land-packages-adelaide" element={<Navigate to="/house-and-land-packages-adelaide" replace />} />
          <Route path="/house-and-land-vs-buying-separately" element={<HouseAndLandVsBuyingSeparatelyPage />} />
          <Route path="/turnkey-vs-standard-packages" element={<HLComparisonPage />} />
          <Route path="/what-can-you-build-for-500k-adelaide" element={<BudgetRealityPage />} />
          <Route path="/what-can-you-build-for-600k-adelaide" element={<BudgetRealityPage />} />
          <Route path="/what-can-you-build-for-650k-adelaide" element={<BudgetRealityPage />} />
          <Route path="/what-can-you-build-for-750k-adelaide" element={<BudgetRealityPage />} />
          <Route path="/what-can-you-build-for-850k-adelaide" element={<BudgetRealityPage />} />
          <Route path="/house-and-land-package-hidden-costs" element={<HLWarningPage />} />
          <Route path="/house-and-land-red-flags" element={<HLWarningPage />} />
          <Route path="/questions-to-ask-house-and-land" element={<HLWarningPage />} />
          <Route path="/first-home-buyer-house-and-land-guide" element={<FirstHomeBuyerHLPage />} />
          
          {/* House & Land Budget Tier Pages */}
          <Route path="/house-and-land-packages-adelaide-under-400k" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-adelaide-under-500k" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-under-500k-adelaide" element={<Navigate to="/house-and-land-packages-adelaide-under-500k" replace />} />
          <Route path="/house-and-land-packages-adelaide-under-600k" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-under-600k-adelaide" element={<Navigate to="/house-and-land-packages-adelaide-under-600k" replace />} />
          <Route path="/house-and-land-packages-adelaide-under-700k" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-under-400k-adelaide" element={<Navigate to="/house-and-land-packages-adelaide-under-400k" replace />} />
          <Route path="/cheapest-house-and-land-packages-adelaide" element={<HLBudgetPage />} />
          <Route path="/cheap-house-and-land-packages-adelaide" element={<Navigate to="/cheapest-house-and-land-packages-adelaide" replace />} />
          <Route path="/house-and-land-packages-sa-for-sale" element={<HLBudgetPage />} />
          <Route path="/house-and-land-sa-for-sale" element={<Navigate to="/house-and-land-packages-sa-for-sale" replace />} />
          <Route path="/house-and-land-packages-sa-prices" element={<Navigate to="/house-and-land-packages-adelaide" replace />} />
          
          {/* House & Land Region Pages */}
          <Route path="/house-and-land-packages-adelaide-northern-suburbs" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-adelaide-western-suburbs" element={<HLBudgetPage />} />
          <Route path="/house-and-land-packages-two-wells" element={<HLBudgetPage />} />
          
          {/* Land Estate Pages (primary path = /land-estate) */}
          <Route path="/land-estate" element={<EstatesIndexPage />} />
          <Route path="/land-estate/:slug" element={<EstatePage />} />

          {/* Pillar guides - cornerstone SEO */}
          <Route path="/builder-selection-checklist" element={<BuilderSelectionChecklistPage />} />
          {/* SA regulator explainers (2026-09-08) - dedicated exact-match pages */}
          <Route path="/hbcf-adelaide-explainer" element={<HbcfAdelaideExplainerPage />} />
          <Route path="/plansa-approval-guide" element={<PlansaApprovalGuidePage />} />
          {/* VIC regulator explainer (2026-09-08) - dedicated DBI exact-match SEO */}
          <Route path="/dbi-melbourne-explainer" element={<DbiMelbourneExplainerPage />} />
          {/* VIC regulator explainer (2026-09-08) - VBA practitioner registration exact-match SEO */}
          <Route path="/vba-builder-registration-melbourne" element={<VbaBuilderRegistrationMelbournePage />} />
          <Route path="/australian-house-by-decade" element={<AustralianHouseByDecadePage />} />
          <Route path="/cost-of-everything-in-a-house" element={<CostOfEverythingPage />} />
          <Route path="/whats-my-street-made-of" element={<SuburbHomeDnaPage />} />
          <Route path="/whats-my-street-made-of/:suburbSlug" element={<SuburbHomeDnaPage />} />
          <Route path="/whats-my-street-made-of/:suburbSlug/since-nan-bought" element={<SuburbTimeMachinePage />} />
          <Route path="/who-pays-for-repairs-when-renting" element={<RentalRepairRulebookPage />} />
          <Route path="/council-quirks" element={<CouncilQuirksPage />} />
          <Route path="/your-house-decoded" element={<YourHouseDecodedPage />} />
          <Route path="/would-this-pass-today" element={<WouldThisPassTodayPage />} />
          <Route path="/home-problems" element={<HomeProblemsPage />} />
          <Route path="/home-problems/:problemSlug" element={<HomeProblemsPage />} />
          <Route path="/what-the-pros-see" element={<WhatTheProsSeePage />} />
          <Route path="/what-the-pros-see/:guideSlug" element={<WhatTheProsSeePage />} />
          <Route path="/how-to-choose-a-builder" element={<Navigate to="/builder-selection-checklist" replace />} />
          <Route path="/builder-checklist-2026" element={<Navigate to="/builder-selection-checklist" replace />} />
          {/* Legacy routes - 301 to the new structure */}
          <Route path="/estates" element={<Navigate to="/land-estate" replace />} />
          <Route path="/estates/:slug" element={<EstateLegacyRedirect />} />
          <Route path="/land-estates" element={<Navigate to="/land-estate" replace />} />
          <Route path="/developments" element={<Navigate to="/land-estate" replace />} />
          
          {/* Suburb-specific building guides */}
          <Route path="/building-in/davoren-park" element={<Navigate to="/building-in/elizabeth" replace />} />
          <Route path="/building-in/penfield" element={<Navigate to="/building-in/mawson-lakes" replace />} />
          <Route path="/building-in/:suburb" element={<SuburbBuildingPage />} />
          <Route path="/new-homes/:suburb" element={<SuburbBuildingPage />} />
          
          {/* Building Type Pages - Main Services */}
          <Route path="/choosing-land" element={<BuildingTypePage />} />
          <Route path="/building-a-new-home" element={<BuildingTypePage />} />
          <Route path="/renovations" element={<BuildingTypePage />} />
          <Route path="/changing-builders" element={<BuildingTypePage />} />
          <Route path="/property-development" element={<BuildingTypePage />} />
          <Route path="/knockdown-rebuild" element={<><SaVicHreflangPair selfPath="/knockdown-rebuild" selfState="SA" siblingPath="/knockdown-rebuild-melbourne" pageId="savic-kdr-sa" /><BuildingTypePage /></>} />

          {/* Outdoor Living pillar pages — 2026-02-28 SEO cluster.
              Each pillar targets a distinct outdoor building intent
              (pool / verandah-pergola / deck / shed / super-pillar)
              and deep-links into the MiniMatcher via /matcher?category=… */}
          <Route path="/swimming-pool-builders-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/verandahs-and-pergolas-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/decking-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/sheds-and-garages-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/outdoor-living-adelaide" element={<OutdoorPillarPage />} />

          {/* Grounds pillars (Tier 8, 2026-09-08) - landscaping/fencing/driveways
              × SA + VIC. Reuses OutdoorPillarPage template + groundsPillarData.js.
              6 URLs total. VIC entries auto-tag via slug suffix. */}
          <Route path="/landscaping-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/landscaping-melbourne" element={<OutdoorPillarPage />} />
          <Route path="/fencing-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/fencing-melbourne" element={<OutdoorPillarPage />} />
          <Route path="/driveways-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/driveways-melbourne" element={<OutdoorPillarPage />} />

          {/* Outdoor Living suburb variants (Sprint 1, 2026-02-28) */}
          <Route path="/pool-builders-in-glenelg" element={<OutdoorSuburbPage />} />
          <Route path="/pool-builders-in-golden-grove" element={<OutdoorSuburbPage />} />
          <Route path="/verandah-builders-in-mount-barker" element={<OutdoorSuburbPage />} />
          <Route path="/verandah-builders-in-modbury" element={<OutdoorSuburbPage />} />
          <Route path="/decking-builders-in-adelaide-hills" element={<OutdoorSuburbPage />} />
          <Route path="/shed-builders-in-gawler" element={<OutdoorSuburbPage />} />

          {/* Renovation suburb variants (Sprint 4, 2026-02-28) - same
              template, distinct data source. All internal-work-only
              framing (never "builder in [suburb]") to avoid SERP
              overlap with home-builder pillar. */}
          <Route path="/kitchen-renovations-in-norwood" element={<OutdoorSuburbPage />} />
          <Route path="/kitchen-renovations-in-unley" element={<OutdoorSuburbPage />} />
          <Route path="/kitchen-renovations-in-burnside" element={<OutdoorSuburbPage />} />
          <Route path="/bathroom-renovations-in-glenelg" element={<OutdoorSuburbPage />} />
          <Route path="/bathroom-renovations-in-prospect" element={<OutdoorSuburbPage />} />
          <Route path="/bathroom-renovations-in-marion" element={<OutdoorSuburbPage />} />

          {/* Adelaide Q&A pillars (Sprint A, 2026-02-28) - AI-Overview-optimised
              question pages. Each route hits the shared AdelaideQuestionPage
              template + data from adelaideQuestionData.js. Multi-state pattern:
              duplicate the file for VIC/NSW/QLD when we seed those states. */}
          <Route path="/how-much-does-it-cost-to-build-a-house-in-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/how-long-does-it-take-to-build-a-house-in-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/is-it-cheaper-to-renovate-or-rebuild-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/what-is-hbcf-building-indemnity-insurance-sa" element={<AdelaideQuestionPage />} />
          <Route path="/what-council-approvals-do-i-need-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/how-much-does-it-cost-to-renovate-in-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/how-do-i-choose-a-builder-in-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/what-is-a-knockdown-rebuild-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/how-much-deposit-do-i-need-for-a-new-build-adelaide" element={<AdelaideQuestionPage />} />
          <Route path="/best-time-of-year-to-build-in-adelaide" element={<AdelaideQuestionPage />} />

          {/* Melbourne Q&A pillars (VIC Phase 1, 2026-03-05) - VIC mirror of the
              Adelaide Q&A cluster. Same template pattern, own data file
              (melbourneQuestionData.js) so both states rank independently and
              SA copy is never touched. */}
          <Route path="/how-much-does-it-cost-to-build-a-house-in-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/how-long-does-it-take-to-build-a-house-in-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/is-it-cheaper-to-renovate-or-rebuild-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/what-is-vba-domestic-building-insurance-vic" element={<MelbourneQuestionPage />} />
          <Route path="/what-council-approvals-do-i-need-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/how-much-does-it-cost-to-renovate-in-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/how-do-i-choose-a-builder-in-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/what-is-a-knockdown-rebuild-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/how-much-deposit-do-i-need-for-a-new-build-melbourne" element={<MelbourneQuestionPage />} />
          <Route path="/best-time-of-year-to-build-in-melbourne" element={<MelbourneQuestionPage />} />

          {/* Granny Flat pillar cluster (Sprint A, 2026-02-28) - reuses
              OutdoorPillarPage template + grannyFlatPillarData.js. SA
              investment case driven by the Dec 2023 rule change unlocking
              separate rental of ancillary dwellings. */}
          <Route path="/granny-flat-builders-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/granny-flat-cost-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/granny-flat-rules-sa-2026" element={<OutdoorPillarPage />} />
          <Route path="/is-a-granny-flat-a-good-investment-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/granny-flat-vs-extension-adelaide" element={<OutdoorPillarPage />} />

          {/* State waitlist for non-SA visitors (Sprint A audit follow-up, 2026-02-28) */}
          <Route path="/waitlist" element={<StateWaitlistPage />} />

          {/* Pool fencing regulations - high-intent AEO page (Sprint 1, 2026-02-28) */}
          <Route path="/pool-fencing-rules-sa" element={<PoolFencingRulesSAPage />} />

          {/* Renovation pillars (Sprint 2, 2026-02-28) - reuses OutdoorPillarPage
              template with distinct data source. Deliberately framed as
              "renovation" not "builder" to avoid SERP overlap with the
              /home-builders-adelaide pillar. */}
          <Route path="/kitchen-renovations-adelaide" element={<OutdoorPillarPage />} />
          <Route path="/bathroom-renovations-adelaide" element={<OutdoorPillarPage />} />
          
          {/* Supporting Pages - Knockdown Rebuild Hub */}
          <Route path="/knockdown-rebuild/when-to-knockdown-vs-renovate" element={<KDRWhenToKnockdown />} />
          <Route path="/knockdown-rebuild-guide" element={<KnockdownRebuildGuide />} />
          <Route path="/sloping-block-guide" element={<SlopingBlockGuidePage />} />
          <Route path="/home-extensions-adelaide" element={<HomeExtensionsAdelaideGuidePage />} />
          <Route path="/extend-or-rebuild-adelaide" element={<><SaVicHreflangPair selfPath="/extend-or-rebuild-adelaide" selfState="SA" siblingPath="/extend-or-rebuild-melbourne" /><ExtendOrRebuildAdelaidePage /></>} />
          <Route path="/renovate-or-rebuild-adelaide" element={<><SaVicHreflangPair selfPath="/renovate-or-rebuild-adelaide" selfState="SA" siblingPath="/renovate-or-rebuild-melbourne" /><RenovateOrRebuildAdelaidePage /></>} />
          <Route path="/extend-or-rebuild-melbourne" element={<><SaVicHreflangPair selfPath="/extend-or-rebuild-melbourne" selfState="VIC" siblingPath="/extend-or-rebuild-adelaide" /><ExtendOrRebuildMelbournePage /></>} />
          <Route path="/renovate-or-rebuild-melbourne" element={<><SaVicHreflangPair selfPath="/renovate-or-rebuild-melbourne" selfState="VIC" siblingPath="/renovate-or-rebuild-adelaide" /><RenovateOrRebuildMelbournePage /></>} />
          <Route path="/project-vs-custom-builder-adelaide" element={<ProjectVsCustomBuilderPage />} />
          <Route path="/narrow-block-builder-adelaide" element={<NarrowBlockBuildersAdelaidePage />} />
          <Route path="/downsizer-home-builders-adelaide" element={<DownsizerHomeBuildersAdelaidePage />} />
          <Route path="/passive-house-builders-adelaide" element={<PassiveHouseBuildersAdelaidePage />} />
          <Route path="/character-home-renovation-adelaide" element={<CharacterHomeRenovationAdelaidePage />} />
          <Route path="/restoring-a-period-home" element={<RestoringPeriodHomePage />} />
          <Route path="/home-styles" element={<HomeStylesHubPage />} />
          <Route path="/home-styles/:slug" element={<HomeStyleGuidePage />} />
          <Route path="/bushfire-builders-adelaide" element={<BushfireBALBuildersAdelaidePage />} />
          <Route path="/duplex-builders-adelaide" element={<DuplexBuildersAdelaidePage />} />
          <Route path="/split-level-builders-adelaide" element={<SplitLevelBuildersAdelaidePage />} />
          <Route path="/modular-prefab-builders-adelaide" element={<ModularPrefabBuildersAdelaidePage /> } />

          {/* Pillar cluster how-tos (45 supporting how-to pages under the 9 pillars) */}
          {/* Home Extensions clusters */}
          <Route path="/home-extensions-adelaide/second-storey-addition-guide" element={<ClusterHowToPage />} />
          <Route path="/home-extensions-adelaide/ground-floor-rear-extension-guide" element={<ClusterHowToPage />} />
          <Route path="/home-extensions-adelaide/planning-approval-guide" element={<ClusterHowToPage />} />
          <Route path="/home-extensions-adelaide/living-through-extension-guide" element={<ClusterHowToPage />} />
          <Route path="/home-extensions-adelaide/1920s-bungalow-extension-guide" element={<ClusterHowToPage />} />
          {/* Narrow Block clusters */}
          <Route path="/narrow-block-builder-adelaide/6m-frontage-design-guide" element={<ClusterHowToPage />} />
          <Route path="/narrow-block-builder-adelaide/side-setbacks-council-guide" element={<ClusterHowToPage />} />
          <Route path="/narrow-block-builder-adelaide/two-storey-infill-guide" element={<ClusterHowToPage />} />
          <Route path="/narrow-block-builder-adelaide/rear-loaded-garage-guide" element={<ClusterHowToPage />} />
          <Route path="/narrow-block-builder-adelaide/tight-site-logistics-guide" element={<ClusterHowToPage />} />
          {/* Downsizer clusters */}
          <Route path="/downsizer-home-builders-adelaide/accessible-single-storey-guide" element={<ClusterHowToPage />} />
          <Route path="/downsizer-home-builders-adelaide/super-contribution-guide" element={<ClusterHowToPage />} />
          <Route path="/downsizer-home-builders-adelaide/new-build-vs-retirement-village-guide" element={<ClusterHowToPage />} />
          <Route path="/downsizer-home-builders-adelaide/right-sizing-block-guide" element={<ClusterHowToPage />} />
          <Route path="/downsizer-home-builders-adelaide/downsizing-move-timeline-guide" element={<ClusterHowToPage />} />
          {/* Passive House clusters */}
          <Route path="/passive-house-builders-adelaide/blower-door-airtightness-guide" element={<ClusterHowToPage />} />
          <Route path="/passive-house-builders-adelaide/mvhr-selection-guide" element={<ClusterHowToPage />} />
          <Route path="/passive-house-builders-adelaide/passivhaus-vs-nathers-guide" element={<ClusterHowToPage />} />
          <Route path="/passive-house-builders-adelaide/all-electric-new-build-guide" element={<ClusterHowToPage />} />
          <Route path="/passive-house-builders-adelaide/passivhaus-glazing-guide" element={<ClusterHowToPage />} />
          {/* Character Home Renovation clusters */}
          <Route path="/character-home-renovation-adelaide/bluestone-villa-renovation-guide" element={<ClusterHowToPage />} />
          <Route path="/character-home-renovation-adelaide/sympathetic-rear-addition-guide" element={<ClusterHowToPage />} />
          <Route path="/character-home-renovation-adelaide/services-renewal-guide" element={<ClusterHowToPage />} />
          <Route path="/character-home-renovation-adelaide/heritage-overlay-guide" element={<ClusterHowToPage />} />
          <Route path="/character-home-renovation-adelaide/original-features-restoration-guide" element={<ClusterHowToPage />} />
          {/* Bushfire clusters */}
          <Route path="/bushfire-builders-adelaide/bal-ratings-sa-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-adelaide/as3959-compliance-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-adelaide/bushfire-rated-windows-cladding-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-adelaide/defendable-space-guide" element={<ClusterHowToPage />} />
          <Route path="/bushfire-builders-adelaide/bal-assessment-guide" element={<ClusterHowToPage />} />
          {/* Duplex clusters */}
          <Route path="/duplex-builders-adelaide/torrens-vs-community-title-guide" element={<ClusterHowToPage />} />
          <Route path="/duplex-builders-adelaide/land-division-title-split-guide" element={<ClusterHowToPage />} />
          <Route path="/duplex-builders-adelaide/party-wall-detailing-guide" element={<ClusterHowToPage />} />
          <Route path="/duplex-builders-adelaide/duplex-setback-rules-guide" element={<ClusterHowToPage />} />
          <Route path="/duplex-builders-adelaide/duplex-finance-guide" element={<ClusterHowToPage />} />
          {/* Split-Level clusters */}
          <Route path="/split-level-builders-adelaide/sloping-block-design-guide" element={<ClusterHowToPage />} />
          <Route path="/split-level-builders-adelaide/retaining-wall-guide" element={<ClusterHowToPage />} />
          <Route path="/split-level-builders-adelaide/cut-fill-vs-piering-guide" element={<ClusterHowToPage />} />
          <Route path="/split-level-builders-adelaide/stormwater-drainage-guide" element={<ClusterHowToPage />} />
          <Route path="/split-level-builders-adelaide/split-level-accessibility-guide" element={<ClusterHowToPage />} />
          {/* Modular Prefab clusters */}
          <Route path="/modular-prefab-builders-adelaide/modular-delivery-guide" element={<ClusterHowToPage />} />
          <Route path="/modular-prefab-builders-adelaide/modular-vs-traditional-guide" element={<ClusterHowToPage />} />
          <Route path="/modular-prefab-builders-adelaide/modular-site-preparation-guide" element={<ClusterHowToPage />} />
          <Route path="/modular-prefab-builders-adelaide/modular-finance-guide" element={<ClusterHowToPage />} />
          <Route path="/modular-prefab-builders-adelaide/modular-council-approval-guide" element={<ClusterHowToPage />} />

          {/* Tier A - 13 Professionals pillar pages (staged: noindex until flipped live) */}
          <Route path="/architects-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/building-designers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/interior-designers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/town-planners-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/land-surveyors-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/energy-assessors-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/structural-engineers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/quantity-surveyors-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/finance-brokers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/conveyancers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/building-lawyers-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/building-insurance-adelaide" element={<ProfessionalPillarPage />} />
          <Route path="/private-certifiers-adelaide" element={<ProfessionalPillarPage />} />

          {/* Tier B - 6 Building-type gap pillar pages (staged noindex) */}
          <Route path="/volume-home-builders-adelaide" element={<BuildingTypePillarPage />} />
          <Route path="/two-storey-builders-adelaide" element={<BuildingTypePillarPage />} />
          <Route path="/first-home-builders-adelaide" element={<BuildingTypePillarPage />} />
          <Route path="/country-home-builders-adelaide" element={<BuildingTypePillarPage />} />
          <Route path="/acreage-home-builders-adelaide" element={<BuildingTypePillarPage />} />
          <Route path="/compare-home-builders-adelaide" element={<BuildingTypePillarPage />} />
          {/* Tier C - 8 project-type pillars (staged noindex) */}
          <Route path="/choosing-land-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/invest-and-develop-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/verandahs-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/landscaping-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/sheds-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/decking-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/swimming-pools-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/new-homes-adelaide" element={<ProjectTypePillarPage />} />
          <Route path="/granny-flat-guide" element={<GrannyFlatGuidePage />} />
          <Route path="/energy-efficient-home-guide" element={<EnergyEfficientHomeGuidePage />} />
          <Route path="/shower-screens-buying-guide" element={<ShowerScreensBuyingGuidePage />} />
          <Route path="/kitchen-benchtops-sinks-tapware-guide" element={<KitchenBenchtopsBuyingGuidePage />} />
          <Route path="/home-security-cameras-buying-guide" element={<HomeSecurityCamerasBuyingGuidePage />} />
          <Route path="/cleaning-tiles-timber-vinyl-floors-guide" element={<CleaningFloorsBuyingGuidePage />} />
          <Route path="/curtains-blinds-buying-guide" element={<CurtainsBlindsBuyingGuidePage />} />
          <Route path="/new-home-fit-out-guide" element={<NewHomeFitOutGuidePage />} />
          <Route path="/backup-power-new-build-guide" element={<BackupPowerNewBuildGuidePage />} />
          <Route path="/home-security-new-build-guide" element={<HomeSecurityNewBuildGuidePage />} />
          <Route path="/window-furnishings-new-build-guide" element={<WindowFurnishingsNewBuildGuidePage />} />
          <Route path="/kitchen-fit-out-new-build-guide" element={<KitchenFitOutNewBuildGuidePage />} />
          <Route path="/guides" element={<GuidesIndexPage />} />
          <Route path="/sa-market-notes" element={<MarketNotesIndexPage />} />
          <Route path="/sa-market-notes/:slug" element={<MarketNoteDetailPage />} />
          <Route path="/new-this-month" element={<NewThisMonthPage />} />
          <Route path="/build-choices" element={<BuildChoicesIndexPage />} />
          <Route path="/compare-options" element={<CompareOptionsPage />} />
          <Route path="/build-choices/:slug" element={<BuildChoiceGuidePage />} />
          <Route path="/cost-to-build-a-house-australia" element={<CostToBuildAustraliaPage />} />
          <Route path="/how-to-build-a-home" element={<HowToBuildAHomePage />} />
          <Route path="/new-home-setup-checklist" element={<NewHomeSetupChecklistPage />} />
          <Route path="/renovation-costs-australia" element={<RenovationCostsAustraliaPage />} />
          <Route path="/knockdown-rebuild-explained" element={<KnockdownRebuildExplainedPage />} />
          <Route path="/first-home-buyer-guide-australia" element={<FirstHomeBuyerAustraliaPage />} />
          <Route path="/granny-flat-costs-australia" element={<GrannyFlatCostsAustraliaPage />} />
          <Route path="/knockdown-rebuild/costs" element={<KDRCosts />} />
          <Route path="/knockdown-rebuild/council-approvals" element={<KDRCouncilApprovals />} />
          <Route path="/knockdown-rebuild/demolition-process" element={<KDRDemolitionProcess />} />
          
          {/* Supporting Pages - Choosing Land Hub */}
          <Route path="/choosing-land/site-costs" element={<LandSiteCosts />} />
          <Route path="/choosing-land/soil-types" element={<LandSoilTypes />} />
          <Route path="/choosing-land/block-assessment" element={<LandBlockAssessment />} />
          
          {/* Supporting Pages - Building a New Home Hub */}
          <Route path="/building-a-new-home/choosing-a-builder" element={<NewHomeBuilderSelection />} />
          <Route path="/building-a-new-home/contracts" element={<NewHomeContracts />} />
          <Route path="/building-a-new-home/build-timeline" element={<NewHomeBuildTimeline />} />
          <Route path="/building-a-new-home/budget" element={<NewHomeBudget />} />
          <Route path="/building-a-new-home/house-and-land-packages" element={<NewHomeHouseAndLand />} />
          
          {/* Supporting Pages - Renovations Hub */}
          <Route path="/renovations/costs" element={<RenovationCosts />} />
          <Route path="/home-improvement" element={<HomeImprovementHub />} />
          <Route path="/swimming-pools/costs" element={<ImprovementCostGuide slug="swimming-pools" />} />
          <Route path="/decking/costs" element={<ImprovementCostGuide slug="decking" />} />
          <Route path="/landscaping/costs" element={<ImprovementCostGuide slug="landscaping" />} />
          <Route path="/verandahs-pergolas/costs" element={<ImprovementCostGuide slug="verandahs-pergolas" />} />
          <Route path="/swimming-pools/choosing-a-builder" element={<ImprovementChoosingGuide slug="swimming-pools" />} />
          <Route path="/decking/choosing-a-builder" element={<ImprovementChoosingGuide slug="decking" />} />
          <Route path="/landscaping/choosing-a-landscaper" element={<ImprovementChoosingGuide slug="landscaping" />} />
          <Route path="/verandahs-pergolas/choosing-a-builder" element={<ImprovementChoosingGuide slug="verandahs-pergolas" />} />
          <Route path="/swimming-pools/planning" element={<ImprovementPlanningGuide slug="swimming-pools" />} />
          <Route path="/decking/planning" element={<ImprovementPlanningGuide slug="decking" />} />
          <Route path="/landscaping/planning" element={<ImprovementPlanningGuide slug="landscaping" />} />
          <Route path="/verandahs-pergolas/planning" element={<ImprovementPlanningGuide slug="verandahs-pergolas" />} />
          <Route path="/renovations/choosing-a-renovator" element={<RenovationChoosingRenovator />} />
          <Route path="/renovations/planning" element={<RenovationPlanning />} />
          <Route path="/renovations/living-through-renovation" element={<RenovationLivingThrough />} />
          
          {/* South Australia Suburb Pages */}
          <Route path="/south-australia" element={<SouthAustraliaPage />} />
          <Route path="/south-australia/adelaide" element={<AdelaidePage />} />
          <Route path="/south-australia/adelaide/unley" element={<UnleyPage />} />
          <Route path="/south-australia/adelaide/norwood" element={<NorwoodPage />} />
          <Route path="/south-australia/adelaide/prospect" element={<ProspectPage />} />
          <Route path="/south-australia/adelaide/glenelg" element={<GlenelgPage />} />
          <Route path="/south-australia/adelaide/brighton" element={<BrightonPage />} />
          <Route path="/south-australia/adelaide/burnside" element={<BurnsidePage />} />
          <Route path="/south-australia/adelaide/mitcham" element={<MitchamPage />} />
          <Route path="/south-australia/adelaide/henley-beach" element={<HenleyBeachPage />} />
          <Route path="/south-australia/adelaide/marden" element={<MardenPage />} />
          <Route path="/south-australia/adelaide/fulham" element={<FulhamPage />} />
          <Route path="/south-australia/adelaide/magill" element={<MagillPage />} />
          <Route path="/south-australia/adelaide/plympton" element={<PlymptonPage />} />
          
          {/* Help and Advice Pages */}
          <Route path="/help" element={<HelpIndexPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/sources" element={<SourceIndexPage />} />
          <Route path="/editorial-notebook" element={<EditorialNotebookPage />} />
          <Route path="/lifecycle/:stageSlug" element={<LifecyclePillarPage />} />
          {/* 2026-02-24: Customer-journey SEO landing pages */}
          <Route path="/journey/:slug" element={<JourneyLandingPage />} />
          {/* 2026-02-24: External state-specific build-dispute help */}
          <Route path="/help/building-disputes" element={<BuildingDisputesPage />} />
          <Route path="/help/building-disputes/:state" element={<BuildingDisputesPage />} />
          <Route path="/help/building-disputes/:state/:issue" element={<BuildingDisputesPage />} />
          <Route path="/help/cheapest-land-south-australia" element={<CheapestLandSA />} />
          <Route path="/help/cheapest-land-to-buy-right-now" element={<CheapestLandNow />} />
          <Route path="/help/which-adelaide-suburbs-will-boom" element={<AdelaideSuburbsBoom />} />
          <Route path="/help/buy-land-off-sa-government" element={<BuyLandSAGov />} />
          <Route path="/help/how-much-money-house-and-land-package" element={<HouseAndLandMoney />} />
          <Route path="/help/build-house-for-600000" element={<BuildHouse600k />} />
          <Route path="/help/house-and-land-packages-worth-it" element={<HouseAndLandWorthIt />} />
          <Route path="/help/are-house-prices-falling-adelaide" element={<HousePricesFalling />} />
          <Route path="/help/best-home-builder-south-australia" element={<BestHomeBuilderSA />} />
          <Route path="/help/cost-to-build-house-adelaide" element={<CostToBuildAdelaide />} />
          <Route path="/help/most-reliable-builder-adelaide" element={<MostReliableBuilder />} />
          <Route path="/help/how-much-does-it-cost-to-build-a-house-adelaide" element={<HouseCostBuildAdelaide />} />
          <Route path="/help/which-suburbs-will-boom-in-adelaide" element={<SuburbsBoomAdelaide />} />
          <Route path="/help/is-it-cheaper-to-build-or-buy-a-house-australia" element={<CheaperBuildOrBuy />} />
          <Route path="/help/which-house-builder-has-the-best-reputation" element={<BuilderBestReputation />} />
          <Route path="/help/top-5-construction-companies" element={<Top5Construction />} />
          <Route path="/help/most-expensive-part-of-building-a-house" element={<MostExpensivePart />} />
          <Route path="/help/best-to-buy-or-build-adelaide" element={<BestBuyOrBuildAdelaide />} />
          <Route path="/help/best-time-of-year-to-build" element={<BestTimeToBuild />} />
          
          {/* Help Category Pages - must come after specific help pages */}
          <Route path="/help/:categorySlug" element={<HelpCategoryPage />} />
          
          {/* Adelaide Flagship Page */}
          <Route path="/adelaide/build-a-new-home" element={<BuildNewHomeAdelaidePage />} />
          
          {/* Area Education Pages */}
          <Route path="/south-australia/adelaide/areas" element={<AreasIndexPage />} />
          <Route path="/south-australia/adelaide/areas/:areaSlug" element={<AreaPageTemplate />} />
          
          {/* Building Type Pages - Additional Types */}
          <Route path="/verandahs-pergolas" element={<BuildingTypePage />} />
          <Route path="/landscaping" element={<BuildingTypePage />} />
          <Route path="/sheds-garages" element={<BuildingTypePage />} />
          <Route path="/decking" element={<BuildingTypePage />} />
          <Route path="/interior-design" element={<BuildingTypePage />} />
          <Route path="/home-design" element={<BuildingTypePage />} />
          <Route path="/swimming-pools" element={<BuildingTypePage />} />
          
          {/* Building Types with /building-types/ prefix (for footer links) */}
          <Route path="/building-types/:slug" element={<BuildingTypePage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/copyright" element={<CopyrightPage />} />
          <Route path="/cookies" element={<CookiePage />} />
          <Route path="/legal/matching-policy" element={<MatchingPolicyPage />} />
          <Route path="/legal/complaints-process" element={<ComplaintsProcessPage />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />

          {/* Supply-side intake - businesses applying to list on BuildPilot */}
          <Route path="/business" element={<BusinessEnquiryPage />} />
          
          {/* Awards & Recognition */}
          <Route path="/awards" element={<AwardsPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/media/:slug" element={<MediaReleasePage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/partners/paylocker" element={<PayLockerHomeownersPage />} />
          <Route path="/partners/paylocker-for-business" element={<PayLockerForBusinessPage />} />
          
          {/* Home Designs */}
          <Route path="/home-designs" element={<HomeDesignsPage />} />
          <Route path="/home-designs/compare" element={<ComparePage />} />
          {/* Editorial design-comparison guides (draft, noindex until approved) */}
          <Route path="/comparing-4-bedroom-home-designs" element={<ComparingFourBedDesignsPage />} />
          <Route path="/home-designs-for-a-12-5-metre-block" element={<DesignsFor12mBlockPage />} />
          <Route path="/comparing-builder-inclusions" element={<ComparingBuilderInclusionsPage />} />
          <Route path="/adelaide-display-home-directory" element={<DisplayHomeDirectoryPage />} />
          <Route path="/data/adelaide-home-building" element={<AdelaideDataPage />} />
          <Route path="/home-designs/:builderSlug" element={<BuilderDesignsPage />} />
          <Route path="/home-designs/:builderSlug/:planSlug" element={<PlanDetailPage />} />
          
          {/* Resource Pages */}
          <Route path="/terminology" element={<TerminologyPage />} />
          <Route path="/terminology/slang" element={<TradieSlangPage />} />
          <Route path="/terminology/regional-words" element={<RegionalWordsPage />} />
          {/* Old terminology URL redirects */}
          <Route path="/terminology/slab-on-ground" element={<Navigate to="/terminology/slab" replace />} />
          <Route path="/terminology/nathers-rating" element={<Navigate to="/terminology/nathers" replace />} />
          <Route path="/terminology/:termSlug" element={<TerminologyPage />} />
          {/* Redirect /glossary to /terminology to avoid duplicate content */}
          <Route path="/glossary" element={<Navigate to="/terminology" replace />} />
          <Route path="/glossary/:termSlug" element={<GlossaryRedirect />} />
          
          {/* Contact Page */}
          <Route path="/contact" element={<ContactPage />} />
          
          {/* Building Inspectors Page */}
          <Route path="/building-inspectors" element={<BuildingInspectorsPage />} />
          <Route path="/adelaidebuildinginspectors" element={<Navigate to="/building-inspectors" replace />} />
          
          {/* Where We Fit In Page */}
          <Route path="/where-we-fit-in" element={<WhereWeFitInPage />} />
          <Route path="/wherewefitin" element={<Navigate to="/where-we-fit-in" replace />} />
          
          {/* Our Builders Page */}
          {/* 2026-07-05 cannibalisation collapse: /trusted-builders now 301s to /home-builders-adelaide (real 301 in serve.json + _redirects). The <Navigate> below covers client-side SPA navigation. /builderpartners + /copy-of-builder-partners collapsed from a chain (previously trusted-builders -> trusted-builders -> home-builders-adelaide) into single-hop redirects. */}
          <Route path="/trusted-builders" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/builderpartners" element={<Navigate to="/home-builders-adelaide" replace />} />
          
          {/* Why BuildPilot Page */}
          <Route path="/why-buildpilot" element={<WhyBuildPilotPage />} />
          <Route path="/whybuildpilot" element={<Navigate to="/why-buildpilot" replace />} />
          
          {/* Placeholder routes - to be implemented */}
          
          {/* Results Page */}
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/buildpilotreviews" element={<Navigate to="/results" replace />} />
          <Route path="/pastandpresentadelaidebuilds" element={<Navigate to="/results" replace />} />
          
          {/* FAQs Page */}
          <Route path="/faqs" element={<FAQsPage />} />
          
          {/* Blog / Building Tips - SAME URLs as old site */}
          <Route path="/buildingtipsandtricks" element={<BlogListingPage />} />
          <Route path="/building-tips" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/post" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/post/" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/post/:slug" element={<BlogArticlePage />} />
          {/* /blog aliases - keep external/inbound links from 404ing */}
          <Route path="/blog" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/blog/:slug" element={<BlogPostRedirect />} />
          
          {/* Search URLs - redirect to blog */}
          <Route path="/search" element={<Navigate to="/buildingtipsandtricks" replace />} />
          
          {/* 301 Redirects for old Wix URLs */}
          {/* People/Team pages - redirect to contact */}
          <Route path="/ourpeople" element={<Navigate to="/contact" replace />} />
          <Route path="/danielsynnott" element={<Navigate to="/contact" replace />} />
          <Route path="/nicholasgoh" element={<Navigate to="/contact" replace />} />
          <Route path="/kianasatala" element={<Navigate to="/contact" replace />} />
          <Route path="/manuelacolangelo" element={<Navigate to="/contact" replace />} />
          <Route path="/businesspartners" element={<Navigate to="/building-partners" replace />} />
          <Route path="/builderpartners" element={<Navigate to="/building-partners" replace />} />
          <Route path="/careers" element={<Navigate to="/contact" replace />} />
          <Route path="/freebuilderchecklist" element={<Navigate to="/questions-to-ask-builders" replace />} />
          
          {/* Legal page redirects */}
          <Route path="/terms-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/securitydataprotectionstatement" element={<Navigate to="/privacy" replace />} />
          
          {/* Building type redirects */}
          <Route path="/buildinganewhomeinadelaide" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/build-a-new-home" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/largehomesadelaide" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/firsthomebuyersadelaide" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/newhomefeasibility" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/homebuildinginspiration" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/propertydevelopment" element={<Navigate to="/property-development" replace />} />
          <Route path="/subdividingtorrenstitle" element={<Navigate to="/property-development" replace />} />
          <Route path="/subdividingcommunitytitle" element={<Navigate to="/property-development" replace />} />
          <Route path="/buildpilotinvest" element={<Navigate to="/property-development" replace />} />
          <Route path="/copy-of-changing-builders" element={<Navigate to="/changing-builders" replace />} />
          <Route path="/copy-of-renovations" element={<Navigate to="/renovations" replace />} />
          <Route path="/downsizing" element={<Navigate to="/renovations" replace />} />
          <Route path="/demolition-process" element={<Navigate to="/renovations" replace />} />
          <Route path="/homedesign" element={<Navigate to="/home-design" replace />} />
          
          {/* Glossary/Terminology redirects */}
          <Route path="/buildingglossary" element={<Navigate to="/terminology" replace />} />
          <Route path="/changes-to-ncc" element={<Navigate to="/terminology" replace />} />
          
          {/* Legal page redirects */}
          <Route path="/cookie-policy" element={<Navigate to="/cookies" replace />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/appterms" element={<Navigate to="/terms" replace />} />
          
          {/* Misc redirects to home */}
          <Route path="/buildpilotapp" element={<Navigate to="/" replace />} />
          <Route path="/category/all-products" element={<Navigate to="/" replace />} />
          <Route path="/buildpilotplatinum" element={<Navigate to="/" replace />} />
          <Route path="/thebetterhomeblueprint" element={<Navigate to="/" replace />} />
          <Route path="/members" element={<Navigate to="/" replace />} />
          <Route path="/product-page/the-better-home-blueprint" element={<Navigate to="/" replace />} />
          <Route path="/product-page/a-simple-guide-to-building-a-home" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/product-page/exploring-building-materials" element={<Navigate to="/terminology" replace />} />
          
          {/* Additional old Wix URL redirects - from Google Search Console */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/pastandpresentprojects" element={<Navigate to="/results" replace />} />
          <Route path="/upgradingyourhome" element={<Navigate to="/renovations" replace />} />
          <Route path="/buildpilotteam" element={<Navigate to="/contact" replace />} />
          <Route path="/azbuildingterminology" element={<Navigate to="/terminology" replace />} />
          <Route path="/copy-of-home-builders-advisory" element={<Navigate to="/where-we-fit-in" replace />} />
          <Route path="/copy-of-builder-partners" element={<Navigate to="/home-builders-adelaide" replace />} />
          <Route path="/buildinginspections" element={<Navigate to="/building-inspectors" replace />} />
          <Route path="/ebook-buy" element={<Navigate to="/contact" replace />} />
          <Route path="/design" element={<Navigate to="/home-design" replace />} />
          <Route path="/terms-conditions" element={<Navigate to="/terms" replace />} />
          
          {/* Old Wix URLs - redirect to appropriate pages */}
          <Route path="/securitydataprotectionstatement" element={<Navigate to="/privacy" replace />} />
          <Route path="/buildingtipsandtricks/search" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/buildingtipsandtricks/page/:pageNum" element={<Navigate to="/buildingtipsandtricks" replace />} />
          <Route path="/interiordesign" element={<Navigate to="/interior-design" replace />} />
          
          {/* Legacy Wix suburb URLs - redirect to nearest available suburb or regional page */}
          <Route path="/building-in/littlehampton" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/hahndorf" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/nairne" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/bridgewater" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/stirling" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/lobethal" element={<Navigate to="/building-in/mount-barker" replace />} />
          <Route path="/building-in/woodside" element={<Navigate to="/building-in/mount-barker" replace />} />
          
          {/* Legacy Wix URLs - 301 redirects to preserve SEO */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/buildpilotplatinum" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/buildpilotreviews" element={<Navigate to="/results" replace />} />
          <Route path="/ourpeople" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/nicholasgoh" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/danielsynnott" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/ebook-buy" element={<Navigate to="/help" replace />} />
          <Route path="/category/all-products" element={<Navigate to="/help" replace />} />
          <Route path="/adelaidebuildinginspectors" element={<Navigate to="/building-inspectors-adelaide" replace />} />
          <Route path="/pastandpresentadelaidebuilds" element={<Navigate to="/results" replace />} />
          <Route path="/azbuildingterminology" element={<Navigate to="/terminology" replace />} />
          <Route path="/changes-to-ncc" element={<Navigate to="/terminology" replace />} />
          <Route path="/demolition-process" element={<Navigate to="/knockdown-rebuild" replace />} />
          <Route path="/buildpilotinvest" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/downsizing" element={<Navigate to="/renovations" replace />} />
          <Route path="/largehomesadelaide" element={<Navigate to="/building-a-new-home" replace />} />
          <Route path="/freebuilderchecklist" element={<Navigate to="/questions-to-ask-builders" replace />} />
          <Route path="/buildpilotteam" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/buildinginspections" element={<Navigate to="/building-inspectors-adelaide" replace />} />
          <Route path="/pastandpresentprojects" element={<Navigate to="/results" replace />} />
          <Route path="/copy-of-changing-builders" element={<Navigate to="/changing-builders" replace />} />
          <Route path="/copy-of-builder-partners" element={<Navigate to="/building-partners" replace />} />
          <Route path="/copy-of-home-builders-advisory" element={<Navigate to="/where-we-fit-in" replace />} />
          <Route path="/copy-of-design" element={<Navigate to="/home-design" replace />} />
          
          {/* Additional legacy Wix URLs - noindex fixes */}
          <Route path="/whatisahomebuildersadvisory" element={<Navigate to="/where-we-fit-in" replace />} />
          <Route path="/buildpilotadvantage" element={<Navigate to="/why-buildpilot" replace />} />
          <Route path="/yourproposal" element={<Navigate to="/contact" replace />} />
          <Route path="/freebuildingdownloads" element={<Navigate to="/help" replace />} />
          <Route path="/reviews" element={<Navigate to="/results" replace />} />
          <Route path="/manuelacolangelo" element={<Navigate to="/why-buildpilot" replace />} />
          
          {/* Wix API and file URLs - redirect to home */}
          <Route path="/_api/*" element={<Navigate to="/" replace />} />
          <Route path="/_files/*" element={<Navigate to="/" replace />} />
          
          {/* Image URLs that shouldn't be indexed - redirect to home */}
          <Route path="/images/:filename" element={<Navigate to="/" replace />} />
          
          {/* Private Pages - NOT linked anywhere, access by direct URL + token only */}
          <Route path="/p/UAklUjJ9rbMGQITCzutU5tSrPtMF_ky9_2L4aP4TScs" element={<EquableFinanceCalculator />} />
          <Route path="/admin/gsc" element={<GSCDashboard />} />

          {/* ===== Workspace (logged-in user area) ===== */}
          {/* Excluded from sitemap.xml and from prerender map - client-only, auth-gated. */}
          <Route path="/workspace" element={<WorkspaceShell />}>
            <Route index element={<WorkspaceWelcomePage />} />
            <Route path="login" element={<WorkspaceLoginPage />} />
            <Route path="signup" element={<WorkspaceSignupPage />} />
            <Route path="forgot-password" element={<WorkspaceForgotPasswordPage />} />
            <Route path="auth/callback" element={<WorkspaceAuthCallbackPage />} />
            <Route
              path="onboarding"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceOnboardingPage />
                </WorkspaceProtectedRoute>
              }
            />
            {/* Post-onboarding decision gates, these mount OUTSIDE the
                WorkspaceLayout shell so the dark-navy full-bleed iOS gate
                takes the whole viewport (no sidebar / tab-bar chrome). */}
            <Route
              path="land-status-gate"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceLandStatusGatePage />
                </WorkspaceProtectedRoute>
              }
            />
            <Route
              path="house-land-offer-gate"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceHouseLandOfferGatePage />
                </WorkspaceProtectedRoute>
              }
            />
            <Route
              path="fhb-gate"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceFhbGatePage />
                </WorkspaceProtectedRoute>
              }
            />
            <Route
              path="legal-consent"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceLegalConsentPage />
                </WorkspaceProtectedRoute>
              }
            />
            <Route
              path="phone-verification"
              element={
                <WorkspaceProtectedRoute>
                  <WorkspacePhoneVerificationPage />
                </WorkspaceProtectedRoute>
              }
            />
            {/* Find-a-Builder wizard, anonymous-friendly entry.
                The page itself gates auth at step 4 (account/contact)
                so a fresh visitor can answer the first three questions
                (build type, state, build goal) before being asked to
                sign up. This dramatically lifts top-of-funnel intent
                capture without compromising the data quality at the
                serious end of the form. */}
            <Route
              path="tools/find-a-builder"
              element={<WorkspaceFindABuilderPage />}
            />
            {/* Paywall / plans - anonymous-friendly so marketing CTAs
                ("See BuildPilot Platinum pricing") can land a logged-out
                visitor straight on the pricing page. Full-bleed (no
                WorkspaceLayout sidebar), matching the iOS paywall port. */}
            <Route path="billing" element={<WorkspaceBillingPage />} />
            <Route path="billing/success" element={<WorkspaceBillingSuccessPage />} />
            <Route path="paywall" element={<WorkspaceBillingPage />} />

            {/* All authenticated tab pages share the WorkspaceLayout shell */}
            <Route
              element={
                <WorkspaceProtectedRoute>
                  <WorkspaceLayout />
                </WorkspaceProtectedRoute>
              }
            >
              <Route path="dashboard" element={<WorkspaceDashboardPage />} />
              <Route
                path="builders"
                element={
                  <WorkspaceComingSoonPage
                    title="Builders"
                    description="Match with Australia's listed builders, compare them side by side, and shortlist your favourites. This tab is unlocking next."
                    testid="workspace-tab-builders"
                  />
                }
              />
              <Route
                path="designs"
                element={<WorkspaceDesignsPage />}
              />
              <Route
                path="designs/:category"
                element={<WorkspaceDesignRouteSelector />}
              />
              <Route
                path="advice"
                element={<WorkspaceAdvicePage />}
              />
              <Route
                path="toolbox"
                element={<WorkspaceToolboxPage />}
              />
              <Route
                path="tools/cost-tracker"
                element={<WorkspaceCostTrackerPage />}
              />
              <Route
                path="tools/home-log"
                element={<WorkspaceHomeLogPage />}
              />
              <Route
                path="home-log"
                element={<WorkspaceHomeLogPage />}
              />
              <Route
                path="tools/milestones"
                element={<WorkspaceMilestonesPage />}
              />
              <Route
                path="tools/checklists"
                element={<WorkspaceChecklistsPage />}
              />
              <Route
                path="tools/documents"
                element={<WorkspaceDocumentsPage />}
              />
              <Route
                path="tools/contacts"
                element={<WorkspaceContactsPage />}
              />
              <Route
                path="tools/intelligence"
                element={<WorkspaceIntelligencePage />}
              />
              <Route
                path="tools/house-and-land-matchmaker"
                element={<WorkspaceHouseAndLandMatchmakerPage />}
              />
              <Route
                path="tools/favourite-homes"
                element={<WorkspaceFavouriteHomesPage />}
              />
              <Route
                path="matches"
                element={<WorkspaceMyMatchesPage />}
              />
              <Route
                path="tools/facade-visualiser"
                element={<WorkspaceFacadeVisualiserPage />}
              />
              <Route
                path="tools/quote-comparison"
                element={<WorkspaceQuoteComparisonPage />}
              />
              <Route
                path="tools/build-progress"
                element={<WorkspaceBuildProgressPage />}
              />
              <Route
                path="tools/get-quote"
                element={<WorkspaceGetQuotePage />}
              />
              <Route
                path="tools/house-and-land"
                element={<WorkspaceHouseAndLandPage />}
              />
              <Route
                path="tools/builder-licence-check"
                element={<WorkspaceBuilderLicenceCheckPage />}
              />
              <Route
                path="tools/building-insurance-check"
                element={<WorkspaceBuildingInsuranceCheckPage />}
              />
              <Route
                path="tools/government-resources"
                element={<WorkspaceGovernmentResourcesPage />}
              />
              <Route
                path="tools/solar-orientation"
                element={<WorkspaceSolarOrientationPage />}
              />
              <Route
                path="tools/lifestyle-discovery"
                element={<WorkspaceLifestyleDiscoveryPage />}
              />
              <Route
                path="tools/fhb-results"
                element={<WorkspaceFhbResultsPage />}
              />
              <Route
                path="tools/defects-scanner"
                element={<WorkspaceDefectsScannerPage />}
              />
              <Route
                path="tools/furniture-planner"
                element={<WorkspaceFurniturePlannerPage />}
              />
              <Route
                path="tools/interior-3d"
                element={<WorkspaceInterior3DPage />}
              />
              <Route
                path="tools/find-a-tradie"
                element={<WorkspaceFindATradiePage />}
              />
              <Route
                path="tools/find-a-supplier"
                element={<WorkspaceFindASupplierPage />}
              />
              <Route
                path="tools/find-a-business"
                element={<WorkspaceFindABusinessPage />}
              />
              {/* Legacy / aliased URLs → redirect to canonical routes.
                  These match testing-agent's "404 sweep" findings and any
                  external/email links that may still point at old slugs. */}
              <Route path="build" element={<Navigate to="/workspace/advice" replace />} />
              <Route path="tools/suppliers" element={<Navigate to="/workspace/tools/find-a-supplier" replace />} />
              <Route path="tools/packages" element={<Navigate to="/workspace/tools/house-and-land" replace />} />
              <Route path="tools/site-supervisor" element={<Navigate to="/workspace/tools/build-progress" replace />} />
              <Route path="tools/faces-recogniser" element={<Navigate to="/workspace/tools/facade-visualiser" replace />} />
              <Route path="tools/project-health" element={<Navigate to="/workspace/tools/intelligence" replace />} />
              <Route path="tools/contacts-hub" element={<Navigate to="/workspace/tools/contacts" replace />} />
              <Route path="checklists" element={<Navigate to="/workspace/tools/checklists" replace />} />
              <Route path="cost-tracker" element={<Navigate to="/workspace/tools/cost-tracker" replace />} />
              <Route path="milestones" element={<Navigate to="/workspace/tools/milestones" replace />} />
              <Route path="my-saved" element={<Navigate to="/workspace/saved" replace />} />
              <Route
                path="account"
                element={<WorkspaceBillingPage />}
              />
              <Route
                path="profile"
                element={<WorkspaceProfilePage />}
              />
              <Route
                path="notifications"
                element={<WorkspaceNotificationsPage />}
              />
              <Route
                path="saved"
                element={<WorkspaceMySavedPage />}
              />
              <Route
                path="settings"
                element={<WorkspaceSettingsPage />}
              />
              <Route
                path="project-setup"
                element={<WorkspaceProjectSetupPage />}
              />
              <Route
                path="help"
                element={<WorkspaceHelpPage />}
              />
              <Route
                path="activity"
                element={<WorkspaceUpdatesPage />}
              />
              <Route
                path="updates"
                element={<WorkspaceUpdatesPage />}
              />
              <Route
                path="advice-chat"
                element={<WorkspaceAdviceChatPage />}
              />
              <Route
                path="get-advice"
                element={<WorkspaceAdviceChatPage />}
              />
              {/* Community / Glossary / Tips, workspace nav entry points
                  that redirect to the existing marketing pages.
                  Website content is the source of truth; we just
                  surface the link from inside the workspace shell. */}
              <Route
                path="community"
                element={<Navigate to="/community" replace />}
              />
              <Route
                path="glossary"
                element={<Navigate to="/terminology" replace />}
              />
              <Route
                path="tips"
                element={<Navigate to="/buildingtipsandtricks" replace />}
              />
              <Route path="legacy-placeholder" element={<WorkspaceDashboardPlaceholder />} />
            </Route>
          </Route>

          {/* Catch-all - redirect unknown URLs to 404 page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <CookieConsent />
        <TermsReacceptBanner />
        <ContactModal />
        <AuthModal />
        </CompareTrayProvider>
        </CompareProvider>
        </FavouritesProvider>
        </AuthProvider>
      </BrowserRouter>
        </AuthModalProvider>
    </ContactModalProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
